import _ from 'lodash'
import moment from 'moment'
import { mapState as piniaMapState } from 'pinia'

import EventBus from '@/components/mixins/event-bus-mixin'
import MediaScheduleMixin from '@/components/mixins/media-schedule-mixin'
import MediaList from '@/components/common/media-list/media-list-tmpl'
import Draggable from 'vuedraggable'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'
import { useFileUploaderStore } from '@/pinia/stores/file-upload/fileUploaderStore'
import { useFileUploadStorage } from '@/composables/file-upload/use-file-upload-storage'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'

export default {
  name: 'scheduleItemFullDetails',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError'
  ],
  components: {
    MediaList,
    Draggable
  },
  props: {
    parentComponentProp: {
      type: Object,
      default () { return {} }
    },
    scheduleItemProp: {
      type: Object,
      default () { return {} }
    },
    selectedDayProp: {
      type: Object,
      default () { return {} }
    },
    updatedMediaEventsProp: {
      default () { return null }
    },
    selectedScheduleTagsProp: {
      default () { return [] }
    },
    canvasRepository: {
      default () { return [] }
    }
  },
  computed: {
    sortTagsDropzoneOptions () {
      return { animation: 250 }
    },
    sortDraggableOptions () {
      /**
       * Replacement for `tag="transition-group" to vuedraggable by using
       * animation instead.
       * Source: https://github.com/SortableJS/vue.draggable.next/issues/140
       */
      return { animation: 250, group: 'description', ghostClass: 'ghost' }
    },
    sortDraggableAddsEvents () {
      /**
       * Replacement for `tag="transition-group" to vuedraggable by using
       * animation instead.
       * Source: https://github.com/SortableJS/vue.draggable.next/issues/140
       */
      return { animation: 250, group: 'description', ghostClass: 'adds-event-ghost' }
    },
    ...piniaMapState(useFileUploaderStore, {
      fileUploaderState: 'uploadedFiles'
    })
  },
  watch: {
    fileUploaderState (newVal) {
      this.handleAddNewMediaFromFileUploader({ fromFileUploader: true, uploadedFiles: newVal })
    },
    scheduleItemProp (newVal) {
      this.scheduleItemOrig = this.filterScheduleItemOrig(newVal)
      if (this.scheduleItemOrig._edit && this.scheduleItemOrig._edit.listOfMediaDataUpdated) {
        this.fetchStatusLink = true
      }
    },
    selectedDayProp (newVal) {
      this.selectedDay = newVal
      this.getScheduleEvents()
    },
    updatedMediaEventsProp (newVal) {
      if (!newVal.playtimeFormSubmitted) return

      const selectedEventRow = this.$utils.clone(this.selectedEventItemRow.eventRows)

      this.getScheduleEvents({}, () => {
        this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(this.scheduleItemFullDetails)

        const autoMultipleSelect = []
        for (const evtRowSrc of selectedEventRow) {
          let currentEventRowItemPosition = this.scheduleItemFullDetails.events.findIndex((eventRow) => { return eventRow.uid === evtRowSrc.uid })
          if (currentEventRowItemPosition < 0) currentEventRowItemPosition = this.scheduleItemFullDetails.events.findIndex((eventRow) => { return eventRow.order === evtRowSrc.order })

          if (currentEventRowItemPosition >= 0) {
            autoMultipleSelect.push({ eventRowItem: this.scheduleItemFullDetails.events[currentEventRowItemPosition], currentIndx: currentEventRowItemPosition })
          }
        }

        if (autoMultipleSelect.length) {
          this.processEventRowSelection({
            action: this.mediaEventRowActionOptions.editMediaSchedule,
            emitEventToParent: false,
            autoMultipleSelect,
            mediaWithUpdatedScheduleData: newVal.updatedMedia
          })
          this.$utils.mainLoader(false)
        } else {
          this.emitEventToParent({ noSelection: true })
          this.resetMediaEventRowSelection()
          this.$utils.mainLoader(false)
        }
      })
    },
    selectedScheduleTagsProp: {
      handler (newVal) {
        this.selectedScheduleTags = newVal
      },
      deep: true
    },
    scheduleItemFullDetails (newVal) {
      // Ajax for iframe preview
      const mediaList = []

      // For Regular schedule
      if (newVal && newVal.events) {
        newVal.events.forEach(item => {
          // Make sure item has preview. No preview possibly happens if media came from fileUploader with converstion status
          if (!item.preview && (item.media && item.media.preview)) {
            item.preview = item.media.preview
          }
          mediaList.push(item)
        })
      }

      // For Smart Adds schedule
      if (newVal && newVal.addsEvents) {
        newVal.addsEvents.forEach(item => {
          // Make sure item has preview. No preview possibly happens if media came from fileUploader with converstion status
          if (!item.preview && (item.media && item.media.preview)) {
            item.preview = item.media.preview
          }
          mediaList.push(item)
        })
      }

      mediaList.push(this.filterScheduleItemOrig(this.scheduleItemProp))

      // Clone newVal as object is mutable in watch
      const vueStoreVal = _.cloneDeep(newVal)
      vueStoreVal.sideBar = { selectedDay: this.selectedDay }
      this.$store.dispatch('setScheduleItemFullDetails', vueStoreVal)
    },
    sortingRequests: {
      handler (newVal) {
        this.emitEvent('SORTING_REQUESTS_CHANGED', newVal)
      },
      deep: true
    }
  },
  setup () {
    const fileUploadStorage = useFileUploadStorage();
    const fileUploaderStore = useFileUploaderStore();

    return { fileUploadStorage, fileUploaderStore };
  },
  data () {
    return {
      error: '',
      enableEditProject: this.$currentUser.getRights().userCreate.editProject,
      enableDuplicateSchedule: this.$currentUser.getRights().userSchedule.changeTarget,
      enableDeleteMedia: true,
      enableEditCalendar: this.$currentUser.getRights().userSchedule.editCalendar,
      showPopIds: this.$currentUser.hasFeatureProofOfPlayExtended(),
      scheduleItemOrig: this.scheduleItemProp,
      scheduleItemFullDetails: null,
      selectedDay: this.selectedDayProp,
      selectedTimeItem: null,
      mediaEventSelection: [],
      mediaEventRowSelected: [],
      selectedEventItemRow: {
        eventRows: [],
        mediaSummary: []
      },
      previousKeyPressedInMediaEventSelection: null,
      previousEventRowSelected: { eventRow: {}, position: 0 },
      previousMediaEventSelected: { mediaEventItem: {}, mediaEventItemPosition: 0 },
      fetchingMediaEventSelectionDetails: false,
      mediaListDialog: {
        isVisible: false,
        menuItemId: '',
        filterType: '',
        filterTypeOptions: [this.$utils.options.mediaFileType.img, this.$utils.options.mediaFileType.video],
        mediaSelection: [],
        mediaItemToHide: [],
        addTo: 'regularSchedule',
        addToOptions: {
          regularSchedule: 'regularSchedule',
          smartAddsSchedule: 'smartAddsSchedule'
        }
      },
      fetchStatusLink: false,
      hoverAllRemoveIconOnSelectedMedia: false,
      newAddedMedia: [],
      newAddedMediaPlaytimeUids: [],
      drag: false,
      mediaEventSortDragTriggered: {
        start: false,
        smartAdds: false
      },
      mediaEventsBeforeSortMoved: {
        events: [],
        smartAdds: false
      },
      mediaEventRowActionOptions: {
        editMediaSchedule: 'editMediaSchedule',
        deletePlaytimeRow: 'deletePlaytimeRow',
        duplicateMediaSchedule: 'duplicateMediaSchedule',
        targetRowAfterNewPlaytimeUpdates: 'targetRowAfterNewPlaytimeUpdates',
        autoSavedPlaytime: 'autoSavedPlaytime',
        playtimeOrderUpdated: 'playtimeOrderUpdated',
        scheduleEventRowSelected: 'scheduleEventRowSelected',
        editMediaProject: 'editMediaProject',
        updateSmartAddsPeriod: 'updateSmartAddsPeriod',
        updateSmartAddsSponsored: 'updateSmartAddsSponsored',
        handleRegularScheduleMediaEventPreviewSelected: 'handleRegularScheduleMediaEventPreviewSelected',
        handleSmartAddsScheduleMediaEventPreviewSelected: 'handleSmartAddsScheduleMediaEventPreviewSelected'
      },
      lastMediaEventItemAddedToCurrentSelectionForDeleteAction: null,
      multipleDeleteEventRowRequestFlag: [],
      additionalMediaFilterTypeInAddMedia: [],
      mediaListTagSliderLedTagItem: null,
      addsEventsDotsOptions: [
        { label: 'first-dot', value: 1 },
        { label: 'second-dot', value: 2 },
        { label: 'third-dot', value: 3 }
      ],
      lockDraggableSortableEventRow: null,
      lockSortable: null,
      selectedScheduleTags: this.selectedScheduleTagsProp,
      enableSmartAdds: this.$currentUser.hasGroupEnterprise(),
      mouseoverMediaItemClass: '',
      disableMouseoverClass: '',
      sortingRequests: [],
      // State of dragging sort event
      isSortingScheduleItem: false
    }
  },
  mixins: [MediaScheduleMixin, UnsavedDataMixin],
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('schedule-item-change', { action, payload: { data } })
      }
    },
    setDefaultData () {
      // Set Edit Project Icon visibility
      if (this.$currentUser.hasGroupExtended() && !this.$currentUser.hasGroupUserAdmin() && !this.$currentUser.hasGroupUserCreate() && !this.$currentUser.hasGroupUserCreateLock()) {
        this.enableEditProject = false
        this.enableDeleteMedia = false
      }

      // Update File Uploader config
      this.fileUploaderStore.setUploaderConfig({
        setDropzoneInMediaListModal: false,
        triggerClickDropzone: false,
        targetComponent: 'schedulePagePlayerSelected',
        playerId: this.scheduleItemOrig.scheduleId
      })

      this.fetchStatusLink = true
      this.getScheduleEvents()
    },
    /**
     * Get either LED or Videowall tag and type it
     */
    getCustomResolutionQueryParameters () {
      const tag = this.mediaListTagSliderLedTagItem
      if (!tag) {
        return ''
      }

      const isVideowall = !_.chain(this.canvasRepository)
        .filter((c) => tag === c.tag.value)
        .filter((c) => c.types.includes('videowall'))
        .isEmpty()
        .value()

      if (isVideowall) {
        return '?videowallTags=' + encodeURIComponent(tag)
      }
      return '?ledTags=' + encodeURIComponent(tag)
    },
    handleSetupButtonClick () {
      this.$router.push(`/setup/${this.scheduleItemOrig.scheduleId}/${this.scheduleItemOrig.name}`, () => {})
    },
    /**
     * Get events from API
     * @param {Function} callback
     */
    getScheduleEvents (params, callback) {
      const getSchedStart = moment(this.selectedDay.fullDate).set({ hour: 0, minute: 0, second: 0 }).format('YYYY-MM-DD[T]HH:mm:ss')
      const getSchedDur = 24 * 3600 // 24H
      if (!params) params = {}

      const apiRequest = () => {
        this.$utils.mainLoader(true)
        this.error = ''

        if (params.hideLoader) this.$utils.mainLoader(false)

        const queryParams = {
          start: getSchedStart,
          dur: getSchedDur,
          width: 512,
          short: 1,
          use_qsa: 1
        }

        this.$api.get(`${CONFIG.API_BASE_URL}/schedule/${this.scheduleItemOrig.scheduleId}`, { params: queryParams })
          .then(response => {
            // Make sure schedule preview is updated
            const scheduleItemOrig = this.$utils.clone(this.scheduleItemOrig)

            scheduleItemOrig.preview = response.data.preview
            scheduleItemOrig.dimensions = scheduleItemOrig.dir !== 'vertical'
              ? '1920x1080'
              : '1080x1920'

            this.scheduleItemOrig = this.filterScheduleItemOrig(scheduleItemOrig)

            this.$dataStore.setMediaOrientation(scheduleItemOrig.dir === 'vertical' ? 'vertical' : 'horizontal')

            // Smart Adds
            if (response.data.addsEvents) {
              response.data.addsEvents = this.filterAddsEvents(response.data.addsEvents)
            }

            this.scheduleItemFullDetails = this.filterScheduleItemFullDetails(response.data)
            this.additionalMediaFilterTypeInAddMedia = []

            if (this.scheduleItemFullDetails.features && this.scheduleItemFullDetails.features.includes('elementi')) {
              this.additionalMediaFilterTypeInAddMedia = ['elementi']
            }

            // Get LED: tags
            const getLEDTag = scheduleItemOrig.tags && scheduleItemOrig.tags.find((tag) => { return tag.includes('LED:') })
            if (getLEDTag) {
              this.mediaListTagSliderLedTagItem = getLEDTag

              // parse out dimension and pass to the template
              const parsedTag = this.$utils.parseLedTag(getLEDTag)
              if (parsedTag.width && parsedTag.height) {
                scheduleItemOrig.dimensions = parsedTag.dimension
                this.scheduleItemOrig.dimensions = parsedTag.dimension
              }
            }

            this.$nextTick(() => {
              this.updateScheduledMediaTimeline()
              this.$utils.updatePageComponentStyle({ name: 'schedulePage' })

              // Add files that are in uploading state
              if (this.fileUploaderState && this.fileUploaderState.files.length) {
                this.handleAddNewMediaFromFileUploader({ fromFileUploader: true, uploadedFiles: this.fileUploaderState })
              }
            })

            if (typeof (callback) === 'function') {
              callback()
            } else {
              this.$utils.mainLoader(false)
            }
          })
          .catch((error) => {
            this.error = error
            this.$utils.mainLoader(false)
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })

            if (error.response.status === 400) {
              this.emitEvent('RESET_SCHEDULE_ITEM_SELECTION', this.scheduleItemOrig)
            }
          })
      }
      apiRequest()
    },
    filterScheduleItemOrig (scheduleItemOrigSrc) {
      const scheduleItemOrig = this.$utils.clone(scheduleItemOrigSrc)

      if (!scheduleItemOrig._edit) {
        scheduleItemOrig._edit = {
          mediaContentStyle: {}
        }
      }

      return scheduleItemOrig
    },
    filterAddsEvents (addsEventsSrc) {
      const addsEvents = this.$utils.clone(addsEventsSrc)

      // Filter events
      for (const indx in addsEvents) {
        const event = addsEvents[indx]

        if (event.adds && !event.adds.repeat) {
          event.adds.repeat = 1
        }

        if (!event._edit) {
          event._edit = {
            media: event.media ? event.media : {},
            isSelected: false,
            sortKey: event.order,
            uid: `eventRow_${event.order}_${event.uid}`,
            hasDuplicateOrder: false,
            mediaUploading: false,
            showPriorityIcon: false,
            mediaRowBackgroundColor: '',
            addsSponsoredTextEnabled: false
          }
        }

        if (event.adds && ('sponsored' in event.adds)) {
          event._edit.addsSponsoredTextEnabled = (typeof event.adds.sponsored === 'string') && (event.adds.sponsored.length > 0)
        } else {
          event._edit.addsSponsoredTextEnabled = true
        }

        if (!event._edit.sortKeyWithIndx) {
          event._edit.sortKeyWithIndx = `${event.order}_${indx}`
        }

        // Set background color
        event._edit.mediaRowBackgroundColor = (event.skin) ? '#d4d4d4' : '#5a6781'
        event._edit.mediaRowBackgroundColorFade = (event.skin) ? '#eeeeee' : '#bdc2cd'

        if ((event.excludes && event.excludes.length) && (event.excludes.indexOf(this.scheduleItemOrig.scheduleId) >= 0)) {
          event._edit.mediaRowBackgroundColor = '#d4d4d4'
          event._edit.mediaRowBackgroundColorFade = '#eeeeee'
        }

        if (event.priority) {
          event._edit.showPriorityIcon = true
        }

        if (event.tag) {
          event._edit.tagOriginal = event.tag // Keep original tag

          if (Array.isArray(event.tag)) {
            event.tag.forEach((tagVal) => {
              if (tagVal === 'smart-tags') tagVal = 'SMART SYNC'
            })
          }

          if (typeof event.tag === 'string') {
            if (event.tag === 'smart-tags') event.tag = 'SMART SYNC'
          }
        }

        event._edit.media = event.media ? event.media : {}
        // Add Media in each event
        if (event.media) {
          event._edit.media = event.media
        } else {
          for (const indxM in event.media) {
            const media = event.media[indxM]
            if (event.resourceId === media.resourceId) {
              event._edit.media = media
            }
          }
        }

        event._edit.title = event.title || event._edit.media.name || ''
        event._edit.mediaUploading = (event._edit.media && event._edit.media.status)

        // Add offTimes if present with grey background in DOM
        const eventTimes = event.times
        if (event.offTimes && event.offTimes.length) {
          for (const offTime of event.offTimes) {
            offTime.offTime = true
            eventTimes.push(offTime)
          }
        }

        // Filter event times and set as selected to true by default
        event.times = this.filterScheduleTimes(eventTimes, true)

        // Set Proof of Play Identifier Value
        event._edit.popId = event?._edit.media.popId ?? ''
        event._edit.popIdDisplay = event._edit.popId

        if (!event._edit.uid) {
          event._edit.uid = this.$utils.generateUniqueID()
        }

        event._edit.preview = event._edit.media.preview

        // Optimistic update
        event._edit.optimisticUpdate = event.optimisticUpdate ? event.optimisticUpdate : null
      }

      return addsEvents
    },
    /**
     * Filter Schedules for for front-end
     * @param {Object} schedule
     */
    filterScheduleItemFullDetails (schedule) {
      const scheduleItemFullDetails = schedule

      scheduleItemFullDetails.times = this.filterScheduleTimes(scheduleItemFullDetails.times)
      const checkDuplicateOrder = (orderVal) => {
        const eventsSrc = this.$utils.clone(scheduleItemFullDetails.events)
        let count = 0
        for (const indx in eventsSrc) {
          if (eventsSrc[indx].order === orderVal) {
            count += 1
          }
        }
        return count > 1
      }

      // Filter events
      for (const indx in scheduleItemFullDetails.events) {
        const event = scheduleItemFullDetails.events[indx]

        if (!event.uid) { event.uid = event.order }

        if (!event._edit) {
          event._edit = {
            media: event.media || {},
            isSelected: false,
            sortKey: event.order,
            uid: `eventRow_${event.order}_${event.uid}`,
            mediaUploading: false,
            hasDuplicateOrder: checkDuplicateOrder(event.order),
            showPriorityIcon: false
          }
        }

        if (!event._edit.sortKeyWithIndx) {
          event._edit.sortKeyWithIndx = `${event.order}_${indx}`
        }

        if (event.priority) {
          event._edit.showPriorityIcon = true
        }

        if (event.tag) {
          event._edit.tagOriginal = event.tag // Keep original tag

          if (Array.isArray(event.tag)) {
            event.tag.forEach((tagVal) => {
              if (tagVal === 'smart-tags') tagVal = 'SMART SYNC'
            })
          }

          if (typeof event.tag === 'string') {
            if (event.tag === 'smart-tags') event.tag = 'SMART SYNC'
          }
        }

        // Add Media in each event
        if (event.media) {
          event._edit.media = event.media
        } else {
          for (const indxM in scheduleItemFullDetails.media) {
            const media = scheduleItemFullDetails.media[indxM]
            if (event.resourceId === media.resourceId) {
              event._edit.media = media
            }
          }
        }
        event._edit.title = event.title || event._edit.media.name || ''
        event._edit.mediaUploading = (event._edit.media && event._edit.media.status)

        event._edit.mediaRowBackgroundColor = (event.skin) ? '#d4d4d4' : this.scheduleItemOrig._edit.backgroundColor
        event._edit.mediaRowBackgroundColorFade = (event.skin) ? '#eeeeee' : this.scheduleItemOrig._edit.backgroundColorFade

        // Add offTimes if present with grey background in DOM
        const eventTimes = event.times
        if (scheduleItemFullDetails.offTimes && scheduleItemFullDetails.offTimes.length) {
          for (const offTime of scheduleItemFullDetails.offTimes) {
            offTime.offTime = true
            eventTimes.push(offTime)
          }
        }

        // Filter event times and set as selected to true by default
        event.times = this.filterScheduleTimes(eventTimes, true)

        // Set Proof of Play Identifier Value
        event._edit.popId = event?._edit.media.popId ?? ''
        event._edit.popIdDisplay = event._edit.popId

        // For Ajax frames
        event._edit.uid = event.uid
        event._edit.iFrameStatus = 'default'
        event._edit.fetchingIframeContent = false
        event._edit.iframeContent = null
        event._edit.preview = event._edit.media.preview

        // Optimistic update
        event._edit.optimisticUpdate = event.optimisticUpdate ? event.optimisticUpdate : null
      }

      // Audio should be on top of the list
      const audioEvents = []
      const bottomEvents = []
      const scheduleEvents = this.$utils.clone(scheduleItemFullDetails.events)
      scheduleEvents.forEach((event, indx) => {
        if (event.audio) {
          event._edit.sortKeyWithIndx = `${event.order}_${indx}`
          audioEvents.push(event)
        } else {
          bottomEvents.push(event)
        }
      })

      scheduleItemFullDetails.events = audioEvents.concat(bottomEvents)

      return scheduleItemFullDetails
    },
    updateScheduledMediaTimeline () {
      // Scroll all media timeline based from the top timeSlider current position.
      const slider = document.getElementById('timeSliderControl')
      if (!slider) return

      const targetPosition = slider.scrollLeft
      const syncEls = document.getElementsByClassName('time-slider-control-sync')
      if (!syncEls.length) return

      // Short-circuit: every element is already at the correct position.
      if (Array.from(syncEls).every(el => el.scrollLeft === targetPosition)) return

      Array.from(syncEls).forEach(el => {
        el.scrollLeft = targetPosition
      })
    },
    getEventRowTagBackgroundColor (params) {
      let res = this.$utils.getBackgroundColor({ indx: params.position })
      const getDetailsFromSelectedPlayerTags = this.selectedScheduleTags.find((t) => { return t.value === params.tag })

      if (getDetailsFromSelectedPlayerTags && getDetailsFromSelectedPlayerTags.backgroundColor) {
        res = this.$utils.handleColorCodeConversion(getDetailsFromSelectedPlayerTags.backgroundColor, 'hex', 'rgba', 0.6)
      }

      return res
    },
    caculateMediaDuration (media) {
      let duration = 10

      if (media.dur) {
        duration = media.dur
      } else if (!media.dur && media.pageDurs) {
        duration = media.pageDurs.reduce((acc, page) => {
          if (page.dur) return acc + page.dur
          else return acc + 10
        }, 0)
      }

      if ((!media.dur && !media.pageDurs) && (media.type && media.type === 'audio-playlist')) {
        duration = '--:--'
      } else {
        duration = this.$utils.formatDurationToMinutesAndSeconds({ duration })
      }

      return duration
    },
    /**
     * @param {Object} time
     */
    handleScheduleTimeItemClick (time) {
      let resetSelection = false

      if (this.selectedTimeItem !== null && this.selectedTimeItem._edit.uid === time._edit.uid) {
        resetSelection = true
      }

      const selectedTime = time
      const selectedTimeUiId = time._edit.uid
      const selectedTimePlaylistId = time.playlistId
      const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)

      // Set selectedTimeItem data
      let selectedTimeItem = null
      if (!resetSelection) {
        selectedTimeItem = time
      }

      this.selectedTimeItem = selectedTimeItem

      // Filter scheduleItemFullDetails times property to mark as selected the clicked time item
      for (const scheduleTimeIndx in scheduleItemFullDetails.times) {
        const scheduleTime = scheduleItemFullDetails.times[scheduleTimeIndx]

        scheduleTime._edit.isSelected = false

        if ((scheduleTime._edit.uid === selectedTimeUiId) && (!resetSelection)) {
          scheduleTime._edit.isSelected = true
        }
      }

      let selectedEvents = scheduleItemFullDetails.events
      // Get selected events based from selected time playlistId
      if (!resetSelection) {
        selectedEvents = []

        for (const playlist of scheduleItemFullDetails.playlists) {
          if (selectedTimePlaylistId === playlist.playlistId) {
            // Get Media ResourceId
            for (const selMIndx in playlist.media) {
              const selectedMediaResId = playlist.media[selMIndx].resourceId

              // Get Events based from media selected resourceId
              for (const eventResIdIndx in scheduleItemFullDetails.events) {
                const eventOption = scheduleItemFullDetails.events[eventResIdIndx]

                if (selectedMediaResId === eventOption.resourceId) {
                  selectedEvents.push(eventOption)
                }
              }
            }
          }
        }
      }

      // Filter Events Property
      const events = this.$utils.clone(scheduleItemFullDetails.events)
      for (const evntIndx in events) {
        const origEvent = events[evntIndx]

        origEvent._edit.isSelected = true

        if (!resetSelection) {
          origEvent._edit.isSelected = false
        }

        // Reset event times selection first
        origEvent.times.forEach((time, indx) => {
          origEvent.times[indx]._edit.isSelected = true

          if (!resetSelection) {
            origEvent.times[indx]._edit.isSelected = false
          }
        })

        // Check if this origEvent is present in the selected events list then filter it
        for (const selEvtIndx in selectedEvents) {
          const selectedEvent = selectedEvents[selEvtIndx]

          if (origEvent.resourceId === selectedEvent.resourceId) {
            origEvent._edit.isSelected = true

            // Filter Event Times based on the selected time item
            for (const eventTimeIndx in origEvent.times) {
              const timesInSelectedEvent = origEvent.times[eventTimeIndx]

              if (timesInSelectedEvent._edit.startTime === selectedTime._edit.startTime) {
                timesInSelectedEvent._edit.isSelected = true
              }

              if (timesInSelectedEvent._edit.endTime === selectedTime._edit.endTime) {
                timesInSelectedEvent._edit.isSelected = true
              }

              if (timesInSelectedEvent.duration === 86400) {
                timesInSelectedEvent._edit.isSelected = true
              }
            }
          }
        }
      }
      scheduleItemFullDetails.events = events

      this.scheduleItemFullDetails = scheduleItemFullDetails
    },
    checkMediaPropertiesUnsavedData (nextStep) {
      const hasUnsavedMediaPropertiesData = typeof (this.parentComponentProp.$refs.scheduleMediaProperties) !== 'undefined' &&
      this.parentComponentProp.$refs.scheduleMediaProperties._data.editedMediaProperties.length > 0

      if (!hasUnsavedMediaPropertiesData) {
        nextStep()
      }

      /**
        If Media Property form has changes then show the confirmation dialog
      */
      if (hasUnsavedMediaPropertiesData) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'warning',
          title: this.$t('confirm.unsavedChanges'),
          alert: {
            message: this.$t('texts.youHaveUnsavedChanges')
          },
          confirmButtonText: this.$t('buttons.continueWithoutSaving'),
          callbacks: {
            eventOKButtonClick: () => {
              nextStep()
            }
          }
        })
      }
    },
    isSmartAddsEvent (event) {
      const res = false

      return res
    },
    handleScheduleContentClickCapture (event) {
      const exemptEl = ['schedule-media-durations', 'duration-tag-val', 'el-tag']
      let isPresentInExempEl = false

      for (const className of exemptEl) {
        if (event.target && $(event.target).hasClass(className)) {
          isPresentInExempEl = true
        }
      }

      // Go back to players list
      if (!isPresentInExempEl) {
        EventBus.$emit('SELECTED_SCHEDULE_HEADER_CLICKED', {})
      }
    },
    handleScheduleItemClickCapture (event) {
      // Some extra spaces to deny entire row on click
      if (event.target && $(event.target).hasClass('deny-row-click-capture')) {
        return
      }

      // Show player preview
      const isPlayerPreviewClicked = event.target && $(event.target).hasClass('active-schedule-preview-cover')
      const selectedSchedule = this.$utils.clone(this.scheduleItemOrig)
      if (isPlayerPreviewClicked && (selectedSchedule._edit && ['unconfigured', 'failed', 'disconnected'].indexOf(selectedSchedule._edit.playerStatus) < 0)) {
        EventBus.$emit('SHOW_PLAYER_PREVIEW_POPUP', {
          preview: this.$utils.clone(selectedSchedule.preview),
          previewOrientation: (selectedSchedule.dir) ? selectedSchedule.dir : 'horizontal',
          source: 'schedulePage',
          ledTag: selectedSchedule.tags ? selectedSchedule.tags.find((tag) => { return tag.includes('LED:') }) : null
        })

        return
      }

      this.checkMediaPropertiesUnsavedData(() => {
        this.emitEvent('RESET_SCHEDULE_ITEM_SELECTION', this.scheduleItemOrig)
      })
    },
    handleMediaRowMouseHoverAndMouseOut (mouseEvent, domEvent) {
      // Do not proceed if there's a moving element(sorting animation) to avoid race between drag&drop and mouse hover that cause the sorting slowdown.
      let countMovingMediaRowElement = 0

      $('.media-row-item').each((indx, el) => {
        const elClassNames = $(el).attr('class').split(/\s+/)
        if (elClassNames.includes('flip-list-move')) {
          countMovingMediaRowElement += 1
        }
      })

      if (countMovingMediaRowElement > 0) return

      // Add a flag to show all delete icon
      if (domEvent.path && $(domEvent.path).hasClass('remove')) {
        if (mouseEvent === 'mouseover') {
          const fromSelectedRow = $(domEvent.path).hasClass('is-parent-row-selected')
          if (fromSelectedRow) {
            this.hoverAllRemoveIconOnSelectedMedia = true
          } else {
            this.hoverAllRemoveIconOnSelectedMedia = false
          }
        } else {
          this.hoverAllRemoveIconOnSelectedMedia = false
        }
      }

      // Add hover class in parent row element
      if (domEvent.target && $(domEvent.target).hasClass('media-row-item-cover')) {
        const parentRowEl = $($(domEvent.target).parent())
        const parentRowElClassname = parentRowEl[0].className.toString().match(/(\beventUid\S+\b)/ig)
        const mouseoverMediaItemClass = parentRowElClassname.length ? parentRowElClassname[0] : null

        if (mouseEvent === 'mouseover') {
          this.mouseoverMediaItemClass = mouseoverMediaItemClass
        } else {
          this.disableMouseoverClass = null
          this.mouseoverMediaItemClass = null
        }
      }
    },
    /**
     * Get media details on each media selection list item
     * @param {Function} callback - next nextStep once all request is done
     */
    getEachMediaDetailsInSelectedMediaSummary (callback) {
      const afterAPIRequest = (responseType, responseData, resourceId) => {
        const selectedEventItemRow = this.$utils.clone(this.selectedEventItemRow)
        let countMediaEventItemWithRequestingStatus = 0

        selectedEventItemRow.mediaSummary.forEach((mediaItem, indx) => {
          if (mediaItem.resourceId === resourceId && responseType === 'success') {
            mediaItem.schedules = (responseData.schedules) ? responseData.schedules : []
            mediaItem._edit._getMediaFullDetails = 'done'
          } else if (mediaItem.resourceId === resourceId && responseType === 'error') {
            mediaItem._edit._getMediaFullDetails = 'done'
          }

          if (mediaItem._edit._getMediaFullDetails === 'requesting') {
            countMediaEventItemWithRequestingStatus += 1
          }
        })

        this.selectedEventItemRow = selectedEventItemRow

        // If all requests are done then run the callback function
        if (countMediaEventItemWithRequestingStatus === 0 && typeof (callback) === 'function') {
          callback()
        }
      }

      let countMediaEventWithFullDetails = 0
      const selectedEventItemRow = this.$utils.clone(this.selectedEventItemRow)

      selectedEventItemRow.mediaSummary.forEach((mediaItem, indx) => {
        if (mediaItem._edit._getMediaFullDetails === null && mediaItem.resourceId) {
          selectedEventItemRow.mediaSummary[indx]._edit._getMediaFullDetails = 'requesting'

          this.selectedEventItemRow = selectedEventItemRow

          const apiRequest = () => {
            const queryParams = {
              scheduleId: this.scheduleItemOrig.scheduleId,
              use_qsa: 1
            }
            this.$api.get(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}`, { params: queryParams })
              .then(response => {
                afterAPIRequest('success', response.data, mediaItem.resourceId)
              })
              .catch((error) => {
                this.$apiError.handleAPIRequestError(error, {
                  originalRequest: () => { apiRequest() },
                  callback: () => { afterAPIRequest('error', error, mediaItem.resourceId) },
                  showDialogNotification: true
                })
              })
          }

          apiRequest()
        } else {
          countMediaEventWithFullDetails += 1
        }
      })

      // All selected Media events has full details so run the callback
      if (countMediaEventWithFullDetails === selectedEventItemRow.mediaSummary.length && typeof (callback) === 'function') {
        callback()
      }
    },
    /**
     * Filter filterScheduleItemFullDetails Object
     * @param {Object} scheduleItemFullDetailsSrc - original Object
     * @param {Strint} type
     */
    filterScheduleItemFullDetailsMediaEventSelection (scheduleItemFullDetailsSrc, params) {
      const scheduleItemFullDetails = this.$utils.clone(scheduleItemFullDetailsSrc)

      for (const eventRow of scheduleItemFullDetails.events) {
        // Update Row selection status
        eventRow._edit.isRowSelected = false
        eventRow._edit.mediaAction = null

        for (const indx in this.selectedEventItemRow.eventRows) {
          if (eventRow.uid === this.selectedEventItemRow.eventRows[indx].uid) {
            eventRow._edit.isRowSelected = true
            eventRow._edit.mediaAction = (typeof (params) === 'object' && params.setRowAction) ? params.setRowAction : null
            break
          }
        }
      }

      if (scheduleItemFullDetails.addsEvents) {
        for (const eventRow of scheduleItemFullDetails.addsEvents) {
          // Update Row selection status
          eventRow._edit.isRowSelected = false
          eventRow._edit.mediaAction = null

          for (const indx in this.selectedEventItemRow.eventRows) {
            if (eventRow.uid === this.selectedEventItemRow.eventRows[indx].uid) {
              eventRow._edit.isRowSelected = true
              eventRow._edit.mediaAction = (typeof (params) === 'object' && params.setRowAction) ? params.setRowAction : null
              break
            }
          }
        }
      }

      return scheduleItemFullDetails
    },
    emitEventToParent (params) {
      let payload = {
        action: (params.action) ? params.action : '',
        ref: this.$utils.generateUniqueID(),
        mediaSelection: this.selectedEventItemRow.mediaSummary,
        scheduleId: this.scheduleItemFullDetails.scheduleId,
        selectedPlaytime: (this.selectedEventItemRow.mediaSummary.length) ? this.selectedEventItemRow.mediaSummary[0]._edit.selectedPlaytime : [],
        playtimeItemsVisible: (this.selectedEventItemRow.mediaSummary.length) ? this.selectedEventItemRow.mediaSummary[0]._edit.allPlaytimeRowUIDSummary : [],
        eventRowSelection: this.selectedEventItemRow.eventRows.filter((eventRow) => { return eventRow.uid && eventRow.order }),
        tagsSummary: []
      }

      // Get tags summary
      this.selectedEventItemRow.eventRows.forEach((eventRow, indx) => {
        if (eventRow.tag) {
          payload.tagsSummary.push({
            value: eventRow.tag,
            backgroundColor: (eventRow.tag === 'SMART SYNC') ? this.$utils.getBackgroundColor({ indx: 1 }) : this.$utils.getBackgroundColor({ indx }),
            uid: eventRow.uid,
            scheduleId: eventRow.scheduleId
          })
        }
      })

      if (params.addParams) {
        payload = { ...payload, ...params.addParams }
      }

      if (params.showMediaMenuSubmitBtn) {
        payload.action = 'showMediaMenuSubmitBtn'
      }

      if (params.noSelection) {
        payload.action = 'resetMediaSelection'
      }

      this.emitEvent('NEW_MEDIA_PLAYTIME_SELECTION', payload)
    },
    handleSelectAllEventRow () {
      const currentMediaToShow = this.scheduleItemFullDetails.events
      let currentEventRowItem = {}
      let eventDom = { shiftKey: true }
      let currentEventRowItemPosition = 0

      if (currentMediaToShow.length === 1) {
        this.previousEventRowSelected = { eventRow: currentMediaToShow[0], position: 0 }
        currentEventRowItem = currentMediaToShow[0]
        eventDom = {}
        currentEventRowItemPosition = 0
      }

      if (currentMediaToShow.length > 1) {
        this.previousEventRowSelected = { eventRow: currentMediaToShow[0], position: 0 }
        currentEventRowItem = currentMediaToShow[currentMediaToShow.length - 1]
        eventDom.shiftKey = true
        currentEventRowItemPosition = currentMediaToShow.length - 1
      }

      this.processEventRowSelection({ eventDom, currentEventRowItem, currentEventRowItemPosition, forceSingleSelect: false, actionType: this.mediaEventRowActionOptions.editMediaProject })

      const hasSelectedRow = this.selectedEventItemRow.eventRows.length > 0
      if (hasSelectedRow) {
        this.$utils.mainLoader(true)
        this.getEachMediaDetailsInSelectedMediaSummary(() => {
          this.emitEventToParent({ action: this.mediaEventRowActionOptions.editMediaSchedule })
          // Close loader if no unsaved data in playtime form
          if (!this.$dataStore.getStore().unsavedChangesInSchedulePagePlaytime) this.$utils.mainLoader(false)
        })
      } else {
        this.emitEventToParent({ noSelection: true })
      }
    },
    processEventRowSelection (params) {
      const currentEventRowItem = params.currentEventRowItem ? params.currentEventRowItem : { _edit: { uid: null, order: null } }
      const currentEventRowItemPosition = (params.currentEventRowItemPosition && params.currentEventRowItemPosition >= 0) ? params.currentEventRowItemPosition : 0
      const forceSingleSelect = params.forceSingleSelect ? params.forceSingleSelect : false
      const mediaWithUpdatedScheduleData = params.mediaWithUpdatedScheduleData ? params.mediaWithUpdatedScheduleData : []

      let isCTRLKeyPressed = (params.eventDom) ? params.eventDom.ctrlKey : false
      let isShiftKeyPressed = (params.eventDom) ? params.eventDom.shiftKey : false
      const selectedEventItemRow = this.$utils.clone(this.selectedEventItemRow)
      let isSelectedEventRowExistInSelection = selectedEventItemRow.eventRows.findIndex((eventItem) => { return eventItem._edit.uid === currentEventRowItem._edit.uid }) >= 0

      if (forceSingleSelect) {
        isCTRLKeyPressed = false
        isShiftKeyPressed = false
        this.previousEventRowSelected = { eventRow: {}, position: 0 }
        selectedEventItemRow.eventRows = []

        if (params.disabledSwitchSelection) {
          isSelectedEventRowExistInSelection = false
        }
      }

      const updateSelectionList = (type, eventRowItem, currentIndx) => {
        const findMediaInSelectionSummary = selectedEventItemRow.mediaSummary.findIndex((mediaItem) => {
          return (typeof eventRowItem === 'object' && eventRowItem._edit && mediaItem.resourceId === eventRowItem._edit.media.resourceId)
        })

        if (type === 'reset') {
          selectedEventItemRow.eventRows = []
          selectedEventItemRow.mediaSummary = []
        }

        if (type === 'add') {
          selectedEventItemRow.eventRows.push(eventRowItem)

          // If the media of this selected row is not yet present in media selection summary then create it, else add only the selected order in media summary
          if (findMediaInSelectionSummary < 0) {
            const mediaItem = {
              ...eventRowItem._edit.media,
              ...{ _edit: { selectedPlaytime: [{ uid: eventRowItem.uid, order: eventRowItem.order }], _getMediaFullDetails: null, isRequesting: false } }
            }

            mediaItem._edit.allPlaytimeRowUIDSummary = []
            this.scheduleItemFullDetails.events.forEach((eventRow) => {
              if (eventRow.resourceId === mediaItem.resourceId) {
                mediaItem._edit.allPlaytimeRowUIDSummary.push(eventRow.uid)
              }
            })
            selectedEventItemRow.mediaSummary.push(mediaItem)
          } else if (findMediaInSelectionSummary >= 0) {
            selectedEventItemRow.mediaSummary[findMediaInSelectionSummary]._edit.selectedPlaytime.push({
              uid: eventRowItem.uid,
              order: eventRowItem.order
            })
          }
        }

        if (type === 'delete') {
          const positionInMediaSummary = selectedEventItemRow.mediaSummary[findMediaInSelectionSummary]._edit.selectedPlaytime.findIndex((item) => { return item.uid === eventRowItem.uid })

          // Remove from selected order in media summary
          if (positionInMediaSummary >= 0) {
            selectedEventItemRow.mediaSummary[findMediaInSelectionSummary]._edit.selectedPlaytime.splice(positionInMediaSummary, 1)
          }
          // Remove media from selection if no order selected assigned to him
          if (findMediaInSelectionSummary >= 0 && selectedEventItemRow.mediaSummary[findMediaInSelectionSummary]._edit.selectedPlaytime.length === 0) {
            selectedEventItemRow.mediaSummary.splice(selectedEventItemRow, findMediaInSelectionSummary)
          }
          selectedEventItemRow.eventRows.splice(currentIndx, 1)
        }
      }
      const keepLastEventRowItemClicked = () => { this.previousEventRowSelected = { eventRow: currentEventRowItem, position: currentEventRowItemPosition } }

      // Single Selection, No Key pressed
      if (!isCTRLKeyPressed && !isShiftKeyPressed) {
        updateSelectionList('reset')

        if (['shift', 'ctrl'].indexOf(this.previousKeyPressedInMediaEventSelection) >= 0) {
          updateSelectionList('add', currentEventRowItem)
        } else {
          if (!isSelectedEventRowExistInSelection) {
            updateSelectionList('add', currentEventRowItem)
          }
        }

        keepLastEventRowItemClicked()
        this.previousKeyPressedInMediaEventSelection = null // Keep as previous key pressed
      }

      // Multiple Selection with CTRL Key Pressed
      if (isCTRLKeyPressed || (isCTRLKeyPressed && !isShiftKeyPressed)) {
        if (!isSelectedEventRowExistInSelection) {
          updateSelectionList('add', currentEventRowItem)
        } else {
          updateSelectionList('delete', currentEventRowItem, currentEventRowItemPosition)
        }

        keepLastEventRowItemClicked()
        this.previousKeyPressedInMediaEventSelection = 'ctrl' // Keep as previous key pressed
      }

      // Multiple Selection with SHIFT Key Pressed
      if (isShiftKeyPressed) {
        const previousEventRowSelected = this.$utils.clone(this.previousEventRowSelected)
        const previousEventRowSelectedPosition = (previousEventRowSelected.position >= 0) ? previousEventRowSelected.position : 0

        updateSelectionList('reset')
        if (previousEventRowSelectedPosition < currentEventRowItemPosition) {
          for (let i = previousEventRowSelectedPosition; i <= currentEventRowItemPosition; i++) {
            updateSelectionList('add', this.scheduleItemFullDetails.events[i])
          }
        }

        if (previousEventRowSelectedPosition > currentEventRowItemPosition) {
          for (let i = currentEventRowItemPosition; i <= previousEventRowSelectedPosition; i++) {
            updateSelectionList('add', this.scheduleItemFullDetails.events[i])
          }
        }
        this.previousKeyPressedInMediaEventSelection = 'shift' // Keep as previous key pressed
      }

      // If select multiple media with details
      if (params.autoMultipleSelect) {
        for (const eventRow of params.autoMultipleSelect) {
          updateSelectionList('add', eventRow.eventRowItem)
        }
      }

      // Find media from summary and update schedule data
      if (mediaWithUpdatedScheduleData) {
        selectedEventItemRow.mediaSummary.forEach((mediaItem, indx) => {
          const findIndxFromUpdatedMediaListData = mediaWithUpdatedScheduleData.findIndex((updatedMedia) => { return updatedMedia.resourceId === mediaItem.resourceId })

          if (findIndxFromUpdatedMediaListData >= 0) {
            const updatedMedia = mediaWithUpdatedScheduleData[findIndxFromUpdatedMediaListData]
            mediaItem.schedules = updatedMedia.schedules ? updatedMedia.schedules : []
            mediaItem._edit._getMediaFullDetails = 'done'
          }
        })
      }

      this.selectedEventItemRow = selectedEventItemRow

      const rowActionParams = {}
      if (params.action) rowActionParams.setRowAction = params.action
      this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(this.scheduleItemFullDetails, rowActionParams)

      // Send event to parent component
      if (params.emitEventToParent) {
        let payload = { action: params.action }

        if (params.appendParamsEmitEventToParent) {
          payload = { ...payload, ...{ addParams: params.appendParamsEmitEventToParent } }
        }

        if (!this.selectedEventItemRow.eventRows.length) {
          payload.noSelection = true
        }
        this.emitEventToParent(payload)
      }
    },
    /**
     * Handle Media Event Row Click
     * @param {String} actionType - (editMediaSchedule, deletePlaytimeRow, duplicateMediaSchedule)
     * @param {Object} currentEventRowItem - selected event row
     * @param {Number} currentEventRowItemPosition - selected event row current position
     * @param {Object} eventDom - $event
     */
    handleMediaEventRowClick (actionType, currentEventRowItem, currentEventRowItemPosition, eventDom) {
      if (eventDom.target && $(eventDom.target).hasClass('media-preview-cover')) return

      const mediaEventRowClickAction = () => {
        const selectEventRowDetails = { ...{ currentEventRowItem, currentEventRowItemPosition, eventDom }, ...{ forceSingleSelect: true }, ...{ action: actionType } }

        // File still uploading
        if (selectEventRowDetails.currentEventRowItem._edit && (selectEventRowDetails.currentEventRowItem._edit.optimisticUpdate && !selectEventRowDetails.currentEventRowItem._edit.optimisticUpdate.uploadFinished)) {
          return
        }

        if (actionType === this.mediaEventRowActionOptions.editMediaProject) {
          if (currentEventRowItem._edit.media.type === 'audio-playlist') {
            this.$router.push(`/project-audio/${currentEventRowItem._edit.media.resourceId}/edit-frame`, () => {})
          } else {
            const queryParams = this.getCustomResolutionQueryParameters()
            this.$dataStore.setStore({ pageRedirectAfterCreateProject: this.$route.fullPath })
            this.$router.push(`/project-edit/${currentEventRowItem._edit.media.resourceId}/edit-frame${queryParams}`, () => {})
          }
        }

        if (!this.enableEditCalendar) return false // User Rights contract level for edit

        // Entire Event Row clicked
        if (actionType === this.mediaEventRowActionOptions.scheduleEventRowSelected) {
          if (currentEventRowItem && currentEventRowItem.lock) return

          selectEventRowDetails.forceSingleSelect = false
          this.processEventRowSelection(selectEventRowDetails)

          if (this.selectedEventItemRow.eventRows.length) {
            this.$utils.mainLoader(true)
            this.getEachMediaDetailsInSelectedMediaSummary(() => {
              this.emitEventToParent({ newSelection: true, action: this.mediaEventRowActionOptions.editMediaSchedule })
              this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(this.scheduleItemFullDetails, { setRowAction: this.mediaEventRowActionOptions.editMediaSchedule })

              // Close loader if no unsaved data in playtime form
              if (!this.$dataStore.getStore().unsavedChangesInSchedulePagePlaytime) this.$utils.mainLoader(false)
            })
          } else {
            this.emitEventToParent({ noSelection: true })
          }
        }

        // Duplicate Event Row Action
        if (actionType === this.mediaEventRowActionOptions.duplicateMediaSchedule) {
          selectEventRowDetails.disabledSwitchSelection = true
          this.processEventRowSelection(selectEventRowDetails)
          this.$utils.mainLoader(true)
          this.getEachMediaDetailsInSelectedMediaSummary(() => {
            this.handleDuplicateEventRowAction(currentEventRowItem, currentEventRowItemPosition)
          })
        }

        // Edit Media Schedule Event Row Action
        if (actionType === this.mediaEventRowActionOptions.editMediaSchedule) {
          selectEventRowDetails.forceSingleSelect = false
          this.processEventRowSelection(selectEventRowDetails)
          const hasSelectedRow = this.selectedEventItemRow.eventRows.length > 0

          if (hasSelectedRow) {
            this.$utils.mainLoader(true)
            this.getEachMediaDetailsInSelectedMediaSummary(() => {
              this.emitEventToParent({ newSelection: true, action: actionType })

              // Close loader if no unsaved data in playtime form
              if (!this.$dataStore.getStore().unsavedChangesInSchedulePagePlaytime) this.$utils.mainLoader(false)
            })
          } else {
            this.emitEventToParent({ noSelection: true })
          }
        }

        // Delete Event Row Action
        if (actionType === this.mediaEventRowActionOptions.deletePlaytimeRow) {
          const processDeleteScheduleEventRow = () => {
            const isNewRowSelectedExistFromCurrentSelection = this.selectedEventItemRow.eventRows.findIndex((currentEventRowSel) => {
              return currentEventRowSel.uid === currentEventRowItem.uid
            }) >= 0
            const hasSingleEventRowSelection = this.selectedEventItemRow.eventRows.length === 1
            const selecteEventRowUIDBeforeDeleteAction = hasSingleEventRowSelection ? this.selectedEventItemRow.eventRows[0].uid : null
            const singleSelectedEventRowBeforeDeleteAction = {
              uid: hasSingleEventRowSelection ? this.selectedEventItemRow.eventRows[0].uid : null,
              order: hasSingleEventRowSelection ? this.selectedEventItemRow.eventRows[0].order : null,
              mediaResourceId: hasSingleEventRowSelection ? this.selectedEventItemRow.eventRows[0].resourceId : null
            }

            // Single Delete that is not part of current event row selection list
            if (!isNewRowSelectedExistFromCurrentSelection) {
              this.$utils.mainLoader(true)
              const getMediaDetails = () => {
                this.$api.get(`${CONFIG.API_BASE_URL}/media/${currentEventRowItem._edit.media.resourceId}?scheduleId=${currentEventRowItem.scheduleId}`)
                  .then(response => {
                    const mediaItem = response.data

                    // Delete playtime from target schedule
                    const findTargetScheduleIndx = mediaItem.schedules.findIndex((currentSchedule) => { return currentSchedule.target === currentEventRowItem.scheduleId })
                    if (findTargetScheduleIndx >= 0) {
                      const targetSchedule = mediaItem.schedules[findTargetScheduleIndx]

                      let playtimeIndxInCustom = -1
                      if (Array.isArray(targetSchedule.custom)) {
                        playtimeIndxInCustom = targetSchedule.custom.findIndex((currentPlaytime) => { return currentPlaytime.uid === currentEventRowItem.uid })
                      }

                      if (playtimeIndxInCustom >= 0) {
                        mediaItem.schedules[findTargetScheduleIndx].custom.splice(playtimeIndxInCustom, 1)
                      } else {
                        // Force to delete schedule if there is only one playtime
                        if (targetSchedule.type === 'daily' || (Array.isArray(targetSchedule.custom) && targetSchedule.custom.length <= 1)) {
                          mediaItem.schedules.splice(findTargetScheduleIndx, 1)
                        }
                      }
                    }

                    // Update media details form API
                    const updateMediaDetails = () => {
                      // Remove status and durtion
                      if (mediaItem.status) {
                        delete mediaItem.status
                        delete mediaItem.dur
                      }
                      this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?scheduleId=${currentEventRowItem.scheduleId}`, mediaItem)
                        .then(response => {
                          const updatedMedia = response.data
                          // Reset media menu data to make sure everything is updated
                          if (hasSingleEventRowSelection) {
                            this.resetMediaEventRowSelection()
                            this.emitEventToParent({ noSelection: true })
                          }

                          // Get schedule events to meke event row list updated
                          this.getScheduleEvents({}, () => {
                            this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(this.scheduleItemFullDetails)

                            // Re-select the previous event row selected before the delete action
                            const currentEventRowItemPosition = this.scheduleItemFullDetails.events.findIndex((eventRow) => { return eventRow.uid === selecteEventRowUIDBeforeDeleteAction })
                            if (currentEventRowItemPosition >= 0) {
                              const currentEventRowItem = this.scheduleItemFullDetails.events[currentEventRowItemPosition]
                              const processEventRowSelectPayload = {
                                currentEventRowItemPosition,
                                currentEventRowItem,
                                forceSingleSelect: true,
                                action: this.mediaEventRowActionOptions.editMediaSchedule,
                                emitEventToParent: false
                              }

                              // If previous selected event row has the same resourceId with updated media, then no need to fetch details from backend, else get details
                              if (singleSelectedEventRowBeforeDeleteAction.resourceId === updatedMedia.resourceId) {
                                processEventRowSelectPayload.mediaWithUpdatedScheduleData = [updatedMedia]
                                processEventRowSelectPayload.emitEventToParent = true
                                this.processEventRowSelection(processEventRowSelectPayload)
                                this.$utils.mainLoader(false)
                              } else {
                                this.processEventRowSelection(processEventRowSelectPayload)
                                this.getEachMediaDetailsInSelectedMediaSummary(() => {
                                  this.emitEventToParent({ newSelection: true, action: this.mediaEventRowActionOptions.editMediaSchedule })
                                  this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(this.scheduleItemFullDetails, { setRowAction: this.mediaEventRowActionOptions.editMediaSchedule })
                                  this.$utils.mainLoader(false)
                                })
                              }
                            } else {
                              this.$utils.mainLoader(false)
                            }
                          })
                        })
                        .catch((error) => {
                          this.$apiError.handleAPIRequestError(error, { originalRequest: () => { updateMediaDetails() }, showDialogNotification: true })
                        })
                    }
                    updateMediaDetails()
                  })
                  .catch((error) => {
                    this.$apiError.handleAPIRequestError(error, { originalRequest: () => { getMediaDetails() }, showDialogNotification: true })
                  })
              }
              getMediaDetails()
            }

            if (isNewRowSelectedExistFromCurrentSelection) {
              this.$utils.mainLoader(true)
              this.selectedEventItemRow.mediaSummary.forEach((mediaItem) => {
                if (mediaItem.resourceId) {
                  const findScheduleTargetIndx = mediaItem.schedules.findIndex((schedule) => { return schedule.target === this.scheduleItemOrig.scheduleId })

                  if (findScheduleTargetIndx >= 0) {
                    const targetSchedule = this.$utils.clone(mediaItem.schedules[findScheduleTargetIndx])

                    if (targetSchedule.custom) {
                      const updatedCutomPlaytime = targetSchedule.custom.filter((playtime) => {
                        return !this.selectedEventItemRow.eventRows.find((selectedEvent) => { return selectedEvent.uid === playtime.uid })
                      })
                      mediaItem.schedules[findScheduleTargetIndx].custom = updatedCutomPlaytime
                    }
                  }

                  const updateMediaDetails = () => {
                    if (this.multipleDeleteEventRowRequestFlag.indexOf(mediaItem.resourceId) < 0) {
                      this.multipleDeleteEventRowRequestFlag.push(mediaItem.resourceId)
                    }

                    // Remove status and durtion
                    if (mediaItem.status) {
                      delete mediaItem.dur
                      delete mediaItem.status
                    }
                    this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`, mediaItem)
                      .then(response => {
                        const findIndxFromRequestFlag = this.multipleDeleteEventRowRequestFlag.indexOf(mediaItem.resourceId)
                        if (findIndxFromRequestFlag >= 0) {
                          this.multipleDeleteEventRowRequestFlag.splice(findIndxFromRequestFlag, 1)
                        }

                        // If all media was udpated then get schedule events to update event row list
                        if (this.multipleDeleteEventRowRequestFlag.length === 0) {
                          this.getScheduleEvents({}, () => {
                            this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(this.scheduleItemFullDetails)
                            this.emitEventToParent({ noSelection: true })
                            this.$utils.mainLoader(false)
                          })
                        }
                      })
                      .catch((error) => {
                        this.$apiError.handleAPIRequestError(error, { originalRequest: () => { updateMediaDetails() }, showDialogNotification: true })
                      })
                  }
                  updateMediaDetails()
                }
              })
            }
          }

          // Show warning if there are event assigned to tags
          const hasAssignedToTagInCurrentSelection = this.$utils.clone(this.selectedEventItemRow.eventRows).filter((event) => { return event.tag })
          if (hasAssignedToTagInCurrentSelection.length || currentEventRowItem.tag) {
            EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
              type: 'warning',
              title: this.$t('label.scheduledToTag'),
              alert: {
                message: this.$t('label.deleteOnAllPlayers')
              },
              confirmButtonText: this.$t('buttons.delete'),
              confirmButtonIcon: 'flowbite:trash-bin-solid',
              callbacks: {
                eventOKButtonClick: () => {
                  processDeleteScheduleEventRow()
                }
              }
            })
          } else {
            processDeleteScheduleEventRow()
          }
        }
      }

      // Check first for unsaved data before processing row action
      this.checkUnsavedData({ nextAction: () => mediaEventRowClickAction() })
    },
    handleMediaEventPreviewClick (actionType, currentEventRowItem, currentEventRowItemPosition, eventDom) {
      if (currentEventRowItem.audio) return

      const preview = this.$utils.clone(currentEventRowItem._edit.media.preview)

      if (currentEventRowItem._edit.media.videopreviews) {
        preview.videopreviews = currentEventRowItem._edit.media.videopreviews
      }

      EventBus.$emit('SHOW_PLAYER_PREVIEW_POPUP', {
        preview,
        previewOrientation: (this.scheduleItemOrig.dir) ? this.scheduleItemOrig.dir : 'horizontal',
        ledTag: this.scheduleItemOrig.tags ? this.scheduleItemOrig.tags.find((tag) => { return tag.includes('LED:') }) : null,
        source: 'schedulePage'
      })
    },
    handleSmartAddsRepeatClick (actionType, currentEventRowItem, currentEventRowItemPosition, repeat, eventDom) {
      const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)
      const origScheduleItemFullDetails = this.$utils.clone(scheduleItemFullDetails)

      if (scheduleItemFullDetails.addsEvents[currentEventRowItemPosition].adds.repeat === repeat) return

      scheduleItemFullDetails.addsEvents[currentEventRowItemPosition].adds.repeat = repeat
      scheduleItemFullDetails.addsEvents = this.filterAddsEvents(scheduleItemFullDetails.addsEvents)

      const eventRowUid = currentEventRowItem.uid
      const mediaResourceId = currentEventRowItem.resourceId
      this.$utils.mainLoader(true)
      const getMediaDetailsRequest = () => {
        this.$api.get(`${CONFIG.API_BASE_URL}/media/${mediaResourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`)
          .then(response => {
            const updateMediaPayload = response.data
            const targetScheduleIndex = updateMediaPayload.schedules.findIndex((schedule) => { return schedule.target === this.scheduleItemOrig.scheduleId })
            const targetSchedule = updateMediaPayload.schedules[targetScheduleIndex]
            const targetPlayTimeIndx = targetSchedule.custom.findIndex((playtime) => { return playtime.uid === eventRowUid })
            const targetPlaytime = targetSchedule.custom[targetPlayTimeIndx]
            targetPlaytime.adds.repeat = repeat

            if (currentEventRowItem.adds.sponsored) {
              targetPlaytime.adds.sponsored = currentEventRowItem.adds.sponsored
            }

            const saveNewMediaDetailsRequest = () => {
              this.$api.put(`${CONFIG.API_BASE_URL}/media/${updateMediaPayload.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`, updateMediaPayload)
                .then(response => {
                  this.scheduleItemFullDetails = scheduleItemFullDetails
                  this.$utils.mainLoader(false)
                  const emitPayload = {
                    playtimeUID: eventRowUid,
                    mediaResourceId,
                    scheduleId: this.scheduleItemOrig.scheduleId,
                    repeat,
                    repeatChanged: true
                  }
                  this.emitEvent('NEW_MEDIA_PLAYTIME_SELECTION_ADDS_CHANGED', emitPayload)
                })
                .catch((error) => {
                  this.$apiError.handleAPIRequestError(error, {
                    originalRequest: () => { saveNewMediaDetailsRequest() },
                    callback: () => { this.scheduleItemFullDetails = origScheduleItemFullDetails },
                    showDialogNotification: true
                  })
                })
            }
            saveNewMediaDetailsRequest()
          })
          .catch((error) => {
            this.$apiError.handleAPIRequestError(error, {
              originalRequest: () => { getMediaDetailsRequest() },
              callback: () => { this.scheduleItemFullDetails = origScheduleItemFullDetails },
              showDialogNotification: true
            })
          })
      }
      getMediaDetailsRequest()
    },
    handleSmartAddsSponsoredActionClick (actionType, currentEventRowItem, currentEventRowItemPosition, eventDom) {
      const scheduleItemFullDetails = this.scheduleItemFullDetails
      const origScheduleItemFullDetails = this.$utils.clone(scheduleItemFullDetails)
      const eventRowUid = currentEventRowItem.uid
      const mediaResourceId = currentEventRowItem.resourceId
      const params = { scheduleId: this.scheduleItemOrig.scheduleId }
      const getMediaDetailsRequest = () => {
        let newSponsoredValue = ''
        if ('sponsored' in currentEventRowItem.adds) {
          if ((typeof currentEventRowItem.adds.sponsored === 'string') && (currentEventRowItem.adds.sponsored.length > 0)) {
            newSponsoredValue = ''
          } else {
            newSponsoredValue = undefined
          }
        }

        // Update Schedule data (optimistic updates)
        scheduleItemFullDetails.addsEvents[currentEventRowItemPosition].adds.sponsored = newSponsoredValue
        if (newSponsoredValue === undefined) {
          delete scheduleItemFullDetails.addsEvents[currentEventRowItemPosition].adds.sponsored
        }
        scheduleItemFullDetails.addsEvents = this.filterAddsEvents(scheduleItemFullDetails.addsEvents)
        this.scheduleItemFullDetails = scheduleItemFullDetails

        this.$api.get(`${CONFIG.API_BASE_URL}/media/${mediaResourceId}`, { params })
          .then(response => {
            const updateMediaPayload = response.data
            const targetScheduleIndex = updateMediaPayload.schedules.findIndex((schedule) => { return schedule.target === this.scheduleItemOrig.scheduleId })
            const targetSchedule = updateMediaPayload.schedules[targetScheduleIndex]
            const targetPlayTimeIndx = targetSchedule.custom.findIndex((playtime) => { return playtime.uid === eventRowUid })
            const targetPlaytime = targetSchedule.custom[targetPlayTimeIndx]

            targetPlaytime.adds.sponsored = newSponsoredValue
            if (newSponsoredValue === undefined) {
              delete targetPlaytime.adds.sponsored
            }

            const saveNewMediaDetailsRequest = () => {
              this.$api.put(`${CONFIG.API_BASE_URL}/media/${updateMediaPayload.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`, updateMediaPayload)
                .then(response => {
                  const emitPayload = {
                    playtimeUID: eventRowUid,
                    mediaResourceId,
                    scheduleId: this.scheduleItemOrig.scheduleId,
                    targetPlaytime,
                    sponsoredChanged: true,
                    sponsored: newSponsoredValue
                  }
                  this.emitEvent('NEW_MEDIA_PLAYTIME_SELECTION_ADDS_CHANGED', emitPayload)
                })
                .catch((error) => {
                  this.$apiError.handleAPIRequestError(error, {
                    originalRequest: () => { saveNewMediaDetailsRequest() },
                    callback: () => { this.scheduleItemFullDetails = origScheduleItemFullDetails },
                    showDialogNotification: true
                  })
                })
            }
            saveNewMediaDetailsRequest()
          })
          .catch((error) => {
            this.$apiError.handleAPIRequestError(error, {
              originalRequest: () => { getMediaDetailsRequest() },
              callback: () => { this.scheduleItemFullDetails = origScheduleItemFullDetails },
              showDialogNotification: true
            })
          })
      }
      getMediaDetailsRequest()
    },
    handleDuplicateEventRowAction (selectedEventRowDetails, selectedEventRowIndx) {
      const selectedEventItemRow = this.$utils.clone(this.selectedEventItemRow)

      // Duplicate playtime and calculate order
      const nextEventRowItemIndx = selectedEventRowIndx + 1
      const topSiblingOrder = selectedEventRowDetails.order
      const bottomSiblingOrder = (this.scheduleItemFullDetails.events[nextEventRowItemIndx]) ? this.scheduleItemFullDetails.events[nextEventRowItemIndx].order : false
      const newCreatedOrder = this.$utils.calculateSortOrderValue({ topSiblingOrder, bottomSiblingOrder })
      const newCreatedUID = `${this.$utils.generateUniqueID()}-${this.scheduleItemFullDetails.events.length + 1}`
      const newPlaytime = { uid: newCreatedUID, order: newCreatedOrder, daily: true, duration: 86400 }

      if (selectedEventRowDetails.tag) newPlaytime.tag = selectedEventRowDetails.tag
      if (selectedEventRowDetails.multi) newPlaytime.multi = selectedEventRowDetails.multi

      if (!selectedEventRowDetails.daily) {
        if (selectedEventRowDetails.duration) newPlaytime.duration = selectedEventRowDetails.duration
        if (selectedEventRowDetails.startDate) newPlaytime.startDate = selectedEventRowDetails.startDate
        if (selectedEventRowDetails.endDate) newPlaytime.endDate = selectedEventRowDetails.endDate
        if (selectedEventRowDetails.startTime) newPlaytime.startTime = selectedEventRowDetails.startTime
        if (selectedEventRowDetails.repeatDays) newPlaytime.repeatDays = selectedEventRowDetails.repeatDays

        delete newPlaytime.daily
      }

      // For Smart tags
      if (selectedEventRowDetails.adds) {
        newPlaytime.adds = selectedEventRowDetails.adds
      }

      const findMediaIndxInSummary = selectedEventItemRow.mediaSummary.findIndex((mediaItem) => { return selectedEventRowDetails.resourceId === mediaItem.resourceId })
      const mediaItem = selectedEventItemRow.mediaSummary[findMediaIndxInSummary]
      const targetScheduleIndx = mediaItem.schedules.findIndex((scheduleItem) => { return selectedEventRowDetails.scheduleId === scheduleItem.target })
      const targetSchedule = mediaItem.schedules[targetScheduleIndx]

      // Convert old schedule data
      if (targetSchedule.type === 'daily') {
        targetSchedule.type = 'custom'
        targetSchedule.custom = [{ daily: true, order: targetSchedule.order }]
      }
      if (targetSchedule.custom && targetSchedule.custom.length && !targetSchedule.custom[0].order) {
        targetSchedule.custom[0].order = targetSchedule.order
      }

      // Make sure to send the original tag
      if (selectedEventItemRow.eventRows.length && selectedEventItemRow.eventRows[0].tag) {
        const tagBackendFormat = selectedEventItemRow.eventRows[0]._edit.tagOriginal.includes('tag-') ? selectedEventItemRow.eventRows[0]._edit.tagOriginal : `tag-${selectedEventItemRow.eventRows[0]._edit.tagOriginal}`
        newPlaytime.tag = tagBackendFormat
      }

      targetSchedule.custom.push(newPlaytime)

      const sendToBackend = () => {
        this.$utils.mainLoader(true)
        // Remove status and durtion
        if (mediaItem.status) {
          if (Object.prototype.hasOwnProperty.call(mediaItem, 'dur')) delete mediaItem.dur
          delete mediaItem.status
        }
        this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?scheduleId=${selectedEventRowDetails.scheduleId}`, mediaItem)
          .then(response => {
            this.getScheduleEvents({}, () => {
              // Auto select the new duplicated schedule
              const currentEventRowItemPosition = this.scheduleItemFullDetails.events.findIndex((eventRow) => { return eventRow.uid === newCreatedUID })
              const currentEventRowItem = this.scheduleItemFullDetails.events[currentEventRowItemPosition]

              this.processEventRowSelection({
                currentEventRowItem,
                currentEventRowItemPosition,
                forceSingleSelect: true,
                action: this.mediaEventRowActionOptions.editMediaSchedule,
                mediaWithUpdatedScheduleData: [response.data],
                emitEventToParent: true
              })
              this.$utils.mainLoader(false)
            })
          })
          .catch((error) => {
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { sendToBackend() }, showDialogNotification: true })
          })
      }
      sendToBackend()
    },
    handleAddMediaClick (params) {
      if (!params) params = {}

      useMixpanel().track('Add media in Schedule')
      const mediaListDialog = this.$utils.clone(this.mediaListDialog)
      mediaListDialog.filterType = 'all'
      mediaListDialog.isVisible = true
      mediaListDialog.mediaSelection = []
      mediaListDialog.mediaItemToHide = []

      // Get List of media to hide
      const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)

      mediaListDialog.mediaItemToHide.push(scheduleItemFullDetails.events.map(eventItemSrc => eventItemSrc.resourceId))

      mediaListDialog.addTo = mediaListDialog.addToOptions.regularSchedule
      if (params.smartAdds) mediaListDialog.addTo = mediaListDialog.addToOptions.smartAddsSchedule

      this.mediaListDialog = mediaListDialog
    },
    handleDialogClose () {
      const mediaListDialog = this.$utils.clone(this.mediaListDialog)
      mediaListDialog.filterType = 'img'
      mediaListDialog.isVisible = false
      mediaListDialog.mediaSelection = []
      mediaListDialog.addTo = mediaListDialog.addToOptions.regularSchedule

      this.mediaListDialog = mediaListDialog

      if (!!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)) {
        $('.project-frame-content').removeClass('hide-project-iframe')
      }

      EventBus.$emit('MEDIA_PICKER_CLOSED')
    },
    filterNewAddedMedia (newMediaListSrc, targetScheduleType) {
      const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)
      let newMediaList = this.$utils.clone(newMediaListSrc)
      let lookupEvents = scheduleItemFullDetails.events

      if (targetScheduleType.smartAddsSchedule) {
        lookupEvents = scheduleItemFullDetails.addsEvents
      }

      // Remove media if has status and present in events list
      newMediaList = newMediaList.filter((media) => {
        const findFromCurrentEvents = lookupEvents.find((event) => { return event.resourceId === media.resourceId })
        return !media.status || !findFromCurrentEvents
      })

      return newMediaList
    },
    handleAddNewMediaFromFileUploader (params) {
      if (!this.scheduleItemFullDetails) return

      if (!params) params = {}

      if (params.fromFileUploader) {
        const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)
        const uploadedFiles = params.uploadedFiles.files

        uploadedFiles.forEach((fileItem, indx) => {
          // Process File
          const isForSchedulePageWithSelectedPlayer = (fileItem._edit && fileItem._edit.targetComponent === 'schedulePagePlayerSelected')
          const fileResourceId = (fileItem._edit && fileItem._edit.uploadRequests) && (fileItem._edit.uploadRequests.createMediaInstanceResponseData.resourceId)
          const fileConversionFinished = (fileItem._edit && fileItem._edit.uploadRequests.conversionFinished)
          const targetScheduleId = (fileItem._edit && fileItem._edit.targetPlayerId) ? fileItem._edit.targetPlayerId : null
          const addToSchedule = (fileItem._edit && fileItem._edit.addToSchedule) ? fileItem._edit.addToSchedule : null
          const addToScheduleRequestStatus = (fileItem._edit && fileItem._edit.addToSchedule) ? fileItem._edit.addToSchedule.addToScheduleRequestStatus : null
          let media = {}

          if (fileConversionFinished) {
            media = fileItem._edit.addToSchedule.media
          } else if (fileResourceId && fileItem._edit.uploadRequests.createMediaInstanceResponseData) {
            media = fileItem._edit.uploadRequests.createMediaInstanceResponseData
          }

          /**
            * Optimistic update: Auto add media and event into the list
          */
          if (isForSchedulePageWithSelectedPlayer && fileResourceId) {
            if (targetScheduleId === this.scheduleItemOrig.scheduleId) {
              for (const incomingEvent of addToSchedule.optimisticUpdate) {
                const updatedMediaPreview = (fileItem._edit.getMediaFullDetails && fileItem._edit.getMediaFullDetails.media) ? fileItem._edit.getMediaFullDetails.media.preview : (fileItem?.media.preview ?? '')

                incomingEvent.media.preview = updatedMediaPreview

                // For Smart Adds
                if (incomingEvent.rowType === 'smartAdds') {
                  // Add smart adds list if not yet exist
                  const getIndexFromSmartAddsList = scheduleItemFullDetails.addsEvents ? scheduleItemFullDetails.addsEvents.findIndex((event) => { return (event._edit && event._edit.optimisticUpdate) && (fileItem.uid === event._edit.optimisticUpdate.fileUploadId) }) : -1
                  if (getIndexFromSmartAddsList < 0) {
                    if (!scheduleItemFullDetails.addsEvents) scheduleItemFullDetails.addsEvents = []
                    scheduleItemFullDetails.addsEvents.push(incomingEvent)
                  } else {
                    const targetEventInList = scheduleItemFullDetails.addsEvents[getIndexFromSmartAddsList]

                    targetEventInList.order = incomingEvent.order
                    targetEventInList.uid = incomingEvent.uid

                    if (!targetEventInList._edit.optimisticUpdate.uploadFinished && fileConversionFinished) {
                      targetEventInList.optimisticUpdate.uploadFinished = true
                      delete targetEventInList.media
                    }

                    targetEventInList.media = media
                  }

                  scheduleItemFullDetails.addsEvents = this.filterAddsEvents(scheduleItemFullDetails.addsEvents)
                } else { // For Regular schedule
                  // Add incoming event into current events list if not yet exist else update the current event data
                  const getIndexFromList = scheduleItemFullDetails.events.findIndex((event) => { return (event._edit && event._edit.optimisticUpdate) && (fileItem.uid === event._edit.optimisticUpdate.fileUploadId) })

                  if (getIndexFromList < 0) {
                    // Group By Type
                    const getAudioEvents = scheduleItemFullDetails.events.filter((event) => { return event.audio })
                    const getOverlayEvents = scheduleItemFullDetails.events.filter((event) => { return event.skin })
                    const getRegularEvents = scheduleItemFullDetails.events.filter((event) => { return !event.skin && !event.audio })

                    if (incomingEvent.audio) {
                      getAudioEvents.push(incomingEvent)
                    } else if (incomingEvent.skin) {
                      getOverlayEvents.push(incomingEvent)
                    } else {
                      getRegularEvents.push(incomingEvent)
                    }
                    scheduleItemFullDetails.events = getAudioEvents.concat(getOverlayEvents).concat(getRegularEvents)
                  } else {
                    const targetEventInList = scheduleItemFullDetails.events[getIndexFromList]

                    targetEventInList.order = incomingEvent.order
                    targetEventInList.uid = incomingEvent.uid

                    if (!targetEventInList._edit.optimisticUpdate.uploadFinished && fileConversionFinished) {
                      targetEventInList.optimisticUpdate.uploadFinished = true
                      delete targetEventInList.media
                    }
                  }

                  // Add or update media in the list
                  const getMediaIndexInList = scheduleItemFullDetails.media.findIndex((m) => { return m.resourceId === fileResourceId })
                  if (getMediaIndexInList < 0) {
                    scheduleItemFullDetails.media.push(media)
                  } else if (getMediaIndexInList >= 0 && fileConversionFinished) {
                    scheduleItemFullDetails.media[getMediaIndexInList] = media
                  }
                }
              }
            }

            // Delete File in local storage
            if (fileConversionFinished && addToScheduleRequestStatus === 'success') {
              // Delete file in local Storage
              this.fileUploadStorage.removeFile(fileItem)
              this.fileUploaderStore.notifyFileDeleted(fileItem)
            }
          }
        })
        this.scheduleItemFullDetails = this.filterScheduleItemFullDetails(scheduleItemFullDetails)
        this.$nextTick(() => {
          this.updateScheduledMediaTimeline()
        })
      }
    },
    /**
     * Hook function if child components changed to handle new added media
     */
    handleAddNewMediaFromMediaListModal (event, newFileSource) {
      if (['MEDIA_SELECTION', 'UPLOADED_MEDIA_FILES'].includes(event.action)) {
        const addNewMediaProcess = {
          getMediaDetailsAndUpdateScheduleData: (params = {}, nextStep) => {
            this.$utils.mainLoader(false)

            const afterGetMediaDetailsAPIRequest = (responseType, responseData, resourceId, newPlaytimeVal) => {
              const newAddedMedia = this.$utils.clone(this.newAddedMedia)
              let countMediaEventItemWithRequestingStatus = 0

              for (const mediaItem of newAddedMedia) {
                if (mediaItem.resourceId === resourceId && responseType === 'success') {
                  mediaItem.schedules = (responseData.schedules) ? responseData.schedules : []

                  // Check if current media has schedule target
                  const scheduleTargetIndx = mediaItem.schedules.findIndex((currentSchedule) => { return currentSchedule.target === this.scheduleItemOrig.scheduleId })
                  if (scheduleTargetIndx >= 0) {
                    if (!mediaItem.schedules[scheduleTargetIndx].custom) {
                      mediaItem.schedules[scheduleTargetIndx].type = 'custom'
                      mediaItem.schedules[scheduleTargetIndx].custom = 'custom'
                    }
                    mediaItem.schedules[scheduleTargetIndx].custom.push(newPlaytimeVal)
                  } else {
                    mediaItem.schedules.push({
                      target: this.scheduleItemOrig.scheduleId,
                      type: 'custom',
                      custom: [newPlaytimeVal]
                    })
                  }
                  mediaItem._edit._getMediaFullDetails = 'done'
                } else if (mediaItem.resourceId === resourceId && responseType === 'error') {
                  mediaItem._edit._getMediaFullDetails = 'done'
                }

                if (mediaItem._edit._getMediaFullDetails === 'requesting') {
                  countMediaEventItemWithRequestingStatus += 1
                }
              }

              this.newAddedMedia = newAddedMedia

              // If all get media details request are done then run the next process
              if (countMediaEventItemWithRequestingStatus === 0) {
                nextStep()
              }
            }

            let countNewUploadMedia = 0
            this.newAddedMedia.forEach((newAddedMediaItem, indx) => {
              if (!newAddedMediaItem._edit) {
                newAddedMediaItem._edit = {}
              }

              // New Playtime to add
              const newPlaytimeVal = { daily: true, order: this.$utils.calculateSortOrderValue({ mediaIndex: indx }), uid: `${this.$utils.generateUniqueID()}-${indx}` }

              // Adjust order value for audio
              if (newAddedMediaItem.type && ['audio-playlist', 'audio'].indexOf(newAddedMediaItem.type) >= 0) {
                if (params.regularSchedule && this.scheduleItemFullDetails.events.length) {
                  const allAudioIndx = []
                  this.scheduleItemFullDetails.events.forEach((event, indx) => { if (event.audio) allAudioIndx.push(indx) })
                  const lastAudioIndx = allAudioIndx.length ? allAudioIndx.length - 1 : null

                  if (lastAudioIndx !== null) {
                    const lastAudioOrderValue = this.scheduleItemFullDetails.events[lastAudioIndx].order
                    const bottomMediaOrderOfLastAudio = this.scheduleItemFullDetails.events[lastAudioIndx + 1] ? this.scheduleItemFullDetails.events[lastAudioIndx + 1].order : null

                    if (lastAudioOrderValue && bottomMediaOrderOfLastAudio) { // middle of the list
                      newAddedMediaItem.order = this.$utils.calculateSortOrderValue({ topSiblingOrder: lastAudioOrderValue, bottomSiblingOrder: bottomMediaOrderOfLastAudio })
                    } else if (lastAudioOrderValue && !bottomMediaOrderOfLastAudio) { // bottom of the list
                      newAddedMediaItem.order = this.$utils.calculateSortOrderValue({ topSiblingOrder: lastAudioOrderValue, bottomSiblingOrder: false })
                    }
                  } else {
                    const firstMediaOrderValue = this.scheduleItemFullDetails.events[0] ? this.scheduleItemFullDetails.events[0].order : null
                    newAddedMediaItem.order = this.$utils.calculateSortOrderValue({ forceToTop: true, previousOrderVal: firstMediaOrderValue })
                  }
                }
              }

              // Adjust order value for overlay
              if (newAddedMediaItem.skin) {
                if (params.regularSchedule && this.scheduleItemFullDetails.events.length) {
                  const allOverlayIndx = []
                  this.scheduleItemFullDetails.events.forEach((event, indx) => { if (event.skin) allOverlayIndx.push(indx) })
                  const lastOverlayIndx = allOverlayIndx.length ? allOverlayIndx.length - 1 : null

                  if (lastOverlayIndx !== null) {
                    const lastOverlayOrderValue = this.scheduleItemFullDetails.events[lastOverlayIndx].order
                    const bottomMediaOrderOfLastAudio = this.scheduleItemFullDetails.events[lastOverlayIndx + 1] ? this.scheduleItemFullDetails.events[lastOverlayIndx + 1].order : null

                    if (lastOverlayOrderValue && bottomMediaOrderOfLastAudio) { // middle of the list
                      newAddedMediaItem.order = this.$utils.calculateSortOrderValue({ topSiblingOrder: lastOverlayOrderValue, bottomSiblingOrder: bottomMediaOrderOfLastAudio })
                    } else if (lastOverlayOrderValue && !bottomMediaOrderOfLastAudio) { // bottom of the list
                      newAddedMediaItem.order = this.$utils.calculateSortOrderValue({ topSiblingOrder: lastOverlayOrderValue, bottomSiblingOrder: false })
                    }
                  } else {
                    const allAudioIndx = []
                    this.scheduleItemFullDetails.events.forEach((event, indx) => { if (event.audio) allAudioIndx.push(indx) })
                    const lastAudioIndx = allAudioIndx.length ? allAudioIndx.length - 1 : null

                    // Check if has audio
                    if (lastAudioIndx !== null) {
                      const lastAudioOrderValue = this.scheduleItemFullDetails.events[lastAudioIndx].order
                      const bottomMediaOrderOfLastAudio = this.scheduleItemFullDetails.events[lastAudioIndx + 1] ? this.scheduleItemFullDetails.events[lastAudioIndx + 1].order : null
                      newAddedMediaItem.order = this.$utils.calculateSortOrderValue({ topSiblingOrder: lastAudioOrderValue, bottomSiblingOrder: bottomMediaOrderOfLastAudio })
                    } else {
                      const firstMediaOrderValue = this.scheduleItemFullDetails.events[0] ? this.scheduleItemFullDetails.events[0].order : null
                      newAddedMediaItem.order = this.$utils.calculateSortOrderValue({ forceToTop: true, previousOrderVal: firstMediaOrderValue })
                    }
                  }
                }
              }

              // If selected date from calendar is not today, then set specific date
              const getSchedStart = moment(this.selectedDay.fullDate).set({ hour: 0, minute: 0, second: 0 }).format('YYYY-MM-DD[T]HH:mm:ss')
              if (!moment().isSame(moment(getSchedStart), 'day')) {
                delete newPlaytimeVal.daily

                newPlaytimeVal.startDate = moment(this.selectedDay.fullDate).format('YYYY-MM-DD')
                newPlaytimeVal.startTime = '00:00'
                newPlaytimeVal.endDate = moment(this.selectedDay.fullDate).format('YYYY-MM-DD')
                newPlaytimeVal.duration = 86400
                newPlaytimeVal.repeatDays = []
              }

              if (this.newAddedMediaPlaytimeUids.indexOf(newPlaytimeVal.uid < 0)) {
                this.newAddedMediaPlaytimeUids.push(newPlaytimeVal.uid)
              }

              // If Smart Adds Schedule
              if (params.smartAddsSchedule) {
                newPlaytimeVal.adds = {
                  repeat: 1
                }
              }

              // If new upload media, then add the new schedule directly, no need to fetch all schedules from API
              if (newAddedMediaItem._newUploadMedia) {
                newAddedMediaItem.schedules = [{
                  target: this.scheduleItemOrig.scheduleId,
                  type: 'custom',
                  custom: [newPlaytimeVal]
                }]
                newAddedMediaItem._edit._getMediaFullDetails = 'done'
                countNewUploadMedia += 1
              }

              /**
               * Optimistic update: Auto add media and event into the list
               */
              const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)
              const newEvent = {
                groupId: newAddedMediaItem.groupId,
                resourceId: newAddedMediaItem.resourceId,
                times: [],
                scheduled: true,
                scheduleId: scheduleItemFullDetails.scheduleId,
                ...newPlaytimeVal,
                media: newAddedMediaItem,
                skin: newAddedMediaItem.skin ? newAddedMediaItem.skin : false,
                audio: newAddedMediaItem.type === 'audio'
              }

              if (params.smartAddsSchedule) {
                scheduleItemFullDetails.addsEvents.push(newEvent)
                scheduleItemFullDetails.addsEvents = this.filterAddsEvents(scheduleItemFullDetails.addsEvents)
              } else {
                delete newEvent.media

                // Group By Type
                const getAudioEvents = scheduleItemFullDetails.events.filter((event) => { return event.audio })
                const getOverlayEvents = scheduleItemFullDetails.events.filter((event) => { return event.skin })
                const getRegularEvents = scheduleItemFullDetails.events.filter((event) => { return !event.skin && !event.audio })

                if (newEvent.audio) {
                  getAudioEvents.push(newEvent)
                } else if (newEvent.skin) {
                  getOverlayEvents.push(newEvent)
                } else {
                  getRegularEvents.push(newEvent)
                }
                scheduleItemFullDetails.events = getAudioEvents.concat(getOverlayEvents).concat(getRegularEvents)

                const existInMediaList = scheduleItemFullDetails.media.find((m) => { return m.resourceId === newAddedMediaItem.resourceId })
                if (!existInMediaList) {
                  scheduleItemFullDetails.media.push(newAddedMediaItem)
                }
              }
              this.scheduleItemFullDetails = this.filterScheduleItemFullDetails(scheduleItemFullDetails)

              // If existing media, then fetch from API to get all schedules
              if (!newAddedMediaItem._newUploadMedia && !newAddedMediaItem._edit._getMediaFullDetails) {
                newAddedMediaItem._edit._getMediaFullDetails = 'requesting'

                const getMediaDetailsRequest = () => {
                  this.$api.get(`${CONFIG.API_BASE_URL}/media/${newAddedMediaItem.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`)
                    .then(response => {
                      afterGetMediaDetailsAPIRequest('success', response.data, newAddedMediaItem.resourceId, newPlaytimeVal)
                    })
                    .catch((error) => {
                      this.$apiError.handleAPIRequestError(error, {
                        originalRequest: () => { getMediaDetailsRequest() },
                        callback: () => { afterGetMediaDetailsAPIRequest('error', error, newAddedMediaItem.resourceId, newPlaytimeVal) },
                        showDialogNotification: true
                      })
                    })
                }

                getMediaDetailsRequest()
              }
            })

            // If all are new upload media, then go to the next step directly, no need to fetch each media schedules
            if (countNewUploadMedia === this.newAddedMedia.length) {
              nextStep()
            }
          },
          saveMediaDetails: (params) => {
            const afterPUTRequestRequest = (responseType, responseData, resourceId) => {
              const newAddedMedia = this.$utils.clone(this.newAddedMedia)
              let countRequestingStatus = 0

              for (const mediaItem of newAddedMedia) {
                if (mediaItem.resourceId === resourceId && responseType === 'success') {
                  mediaItem.schedules = (responseData.schedules) ? responseData.schedules : []
                  mediaItem._edit._saveNewAddedSchedule = 'done'
                } else if (mediaItem.resourceId === resourceId && responseType === 'error') {
                  mediaItem._edit._saveNewAddedSchedule = 'done'
                }

                if (mediaItem._edit._saveNewAddedSchedule === 'requesting') {
                  countRequestingStatus += 1
                }
              }

              this.newAddedMedia = newAddedMedia

              // Media are added into the schedule
              if (countRequestingStatus === 0) {
                this.getScheduleEvents({ hideLoader: true }, () => {
                  this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(this.scheduleItemFullDetails)
                  this.resetMediaEventRowSelection()

                  // Set New Added Media as new selection
                  const autoMultipleSelect = []
                  for (const newAddedUID of this.newAddedMediaPlaytimeUids) {
                    const currentEventRowItemPosition = this.scheduleItemFullDetails.events.findIndex((eventRow) => { return eventRow.uid === newAddedUID })

                    if (currentEventRowItemPosition >= 0) {
                      autoMultipleSelect.push({ eventRowItem: this.scheduleItemFullDetails.events[currentEventRowItemPosition], currentIndx: currentEventRowItemPosition })
                    }
                  }

                  if (autoMultipleSelect.length) {
                    this.processEventRowSelection({
                      action: this.mediaEventRowActionOptions.editMediaSchedule,
                      emitEventToParent: true,
                      forceSingleSelect: false,
                      autoMultipleSelect,
                      mediaWithUpdatedScheduleData: newAddedMedia
                    })

                    // Close loader if no unsaved data in playtime form
                    if (!this.$dataStore.getStore().unsavedChangesInSchedulePagePlaytime) this.$utils.mainLoader(false)
                  } else {
                    this.emitEventToParent({ noSelection: true })
                    this.resetMediaEventRowSelection()
                    this.$utils.mainLoader(false)
                  }

                  // Reset new Added media and UIDS data
                  this.newAddedMedia = []
                  this.newAddedMediaPlaytimeUids = []
                  this.$utils.mainLoader(false)

                  // Scroll media content to bottom
                  this.$nextTick(_ => {
                    if (params.regularSchedule) {
                      const mediaEventRowContentEl = $('#scheduleItemFullDetails').find('.media-content')
                      const scrollHeight = mediaEventRowContentEl.prop('scrollHeight')
                      mediaEventRowContentEl.scrollTop(scrollHeight)
                    }
                  })
                })
              }
            }

            // Save each media item
            for (const newAddedMediaItem of this.newAddedMedia) {
              if (!newAddedMediaItem._edit._saveNewAddedSchedule) {
                newAddedMediaItem._edit._saveNewAddedSchedule = 'requesting'

                const saveNewMediaDetailsRequest = () => {
                  const requestData = {
                    name: newAddedMediaItem.name ? newAddedMediaItem.name : '',
                    schedules: newAddedMediaItem.schedules ? newAddedMediaItem.schedules : [],
                    tags: newAddedMediaItem.tags ? newAddedMediaItem.tags : [],
                    validity: newAddedMediaItem.validity ? newAddedMediaItem.validity : {}
                  }

                  this.$utils.mainLoader(false)
                  this.$api.put(`${CONFIG.API_BASE_URL}/media/${newAddedMediaItem.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`, requestData)
                    .then(response => {
                      afterPUTRequestRequest('success', response.data, newAddedMediaItem.resourceId)
                    })
                    .catch((error) => {
                      this.$apiError.handleAPIRequestError(error, {
                        originalRequest: () => { saveNewMediaDetailsRequest() },
                        callback: () => { afterPUTRequestRequest('error', error, newAddedMediaItem.resourceId) }
                      })
                    })
                }
                saveNewMediaDetailsRequest()
              }
            }
          }
        }

        this.$utils.mainLoader(true)
        const getMediaScheduleParams = { regularSchedule: true, smartAddsSchedule: false }

        if (newFileSource && newFileSource === 'media-list-modal') {
          getMediaScheduleParams.regularSchedule = (this.mediaListDialog.addTo === this.mediaListDialog.addToOptions.regularSchedule)
          getMediaScheduleParams.smartAddsSchedule = (this.mediaListDialog.addTo === this.mediaListDialog.addToOptions.smartAddsSchedule)
        } else if (newFileSource && newFileSource === 'smartAddsScheduleFileDropzone') {
          getMediaScheduleParams.regularSchedule = false
          getMediaScheduleParams.smartAddsSchedule = true
        }

        this.newAddedMedia = this.filterNewAddedMedia(event.payload.data, getMediaScheduleParams)

        addNewMediaProcess.getMediaDetailsAndUpdateScheduleData(getMediaScheduleParams, () => {
          addNewMediaProcess.saveMediaDetails(getMediaScheduleParams)
        })
        this.handleDialogClose()
      } else if (event.action === 'HIDE_MEDIA_LIST_DIALOG') {
        this.handleDialogClose()
      }
    },
    resetMediaEventRowSelection () {
      const selectedEventItemRow = this.$utils.clone(this.selectedEventItemRow)
      selectedEventItemRow.eventRows = []
      selectedEventItemRow.mediaSummary = []
      this.selectedEventItemRow = selectedEventItemRow
      this.previousEventRowSelected = { eventRow: {}, position: 0 }
      this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(this.scheduleItemFullDetails)
    },
    handleMediaEventSortClone (params) {
      this.lockDraggableSortableEventRow = 'smartAddsScheduleSortable'

      if (params && params.smartAdds) {
        this.lockDraggableSortableEventRow = 'regularScheduleSortable'
      }

      this.removeUnsavedDataFlagInLocalStorage()
    },
    /**
     * Hook function when media event sort start moving
     */
    handleMediaEventSortStart (params) {
      this.isSortingScheduleItem = true
      const mediaEventSortDragTriggered = { start: true }
      const mediaEventsBeforeSortMoved = {
        events: this.$utils.clone(this.scheduleItemFullDetails.events)
      }

      if (params.smartAdds) {
        mediaEventSortDragTriggered.smartAdds = true
        mediaEventsBeforeSortMoved.events = this.$utils.clone(this.scheduleItemFullDetails.addsEvents)
        mediaEventsBeforeSortMoved.smartAdds = true
      }

      this.mediaEventSortDragTriggered = mediaEventSortDragTriggered
      this.mediaEventsBeforeSortMoved = mediaEventsBeforeSortMoved
      EventBus.$emit('SORTING_START', { schedulePage: true })
    },
    /**
     * Hook function for schedule item sort drag to keep dragging
     * state enabled to be used in tags dropzone to be disabled
     * when dragging schedule item
     */
    handleMediaEventSortMove () {
      this.isSortingScheduleItem = true
      return true
    },
    /**
     * Hook function on media event row sort move end
     * @param {Object} sortEvtVal - new moved element details
     */
    handleMediaEventSortEnd (sortEvtVal) {
      this.isSortingScheduleItem = false
      this.mediaEventSortDragTriggered = { start: false }
      this.lockDraggableSortableEventRow = null
      EventBus.$emit('SORTING_END', { schedulePage: true })

      // Previous Data
      const mediaEventsBeforeSortMoved = this.$utils.clone(this.mediaEventsBeforeSortMoved)
      const previousMediaEventRowsBeforeSort = this.$utils.clone(mediaEventsBeforeSortMoved.events)
      const previousIndexInList = sortEvtVal.oldIndex
      const previousOrderVal = previousMediaEventRowsBeforeSort[previousIndexInList].order

      // Do not proceed of schedule event is locked
      if (previousMediaEventRowsBeforeSort[previousIndexInList].lock) {
        if (mediaEventsBeforeSortMoved.smartAdds) {
          this.scheduleItemFullDetails.addsEvents = mediaEventsBeforeSortMoved.events
        } else {
          this.scheduleItemFullDetails.events = mediaEventsBeforeSortMoved.events
        }

        this.mediaEventsBeforeSortMoved = { events: [] }
        return
      }

      // Do not proceed of schedule event media uploading
      if (previousMediaEventRowsBeforeSort[previousIndexInList]._edit.optimisticUpdate && (!previousMediaEventRowsBeforeSort[previousIndexInList]._edit.optimisticUpdate.uploadFinished && !previousMediaEventRowsBeforeSort[previousIndexInList]._edit.optimisticUpdate.schedulePlaytimeAdded)) {
        if (mediaEventsBeforeSortMoved.smartAdds) {
          this.scheduleItemFullDetails.addsEvents = mediaEventsBeforeSortMoved.events
        } else {
          this.scheduleItemFullDetails.events = mediaEventsBeforeSortMoved.events
        }

        this.mediaEventsBeforeSortMoved = { events: [] }
        return
      }

      // Do not proceed if there's pending sorting request
      if (this.sortingRequests.find((req) => req.order === previousOrderVal)) {
        return
      }

      // Current Data
      const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)
      let scheduleItemFullDetailsEvents = scheduleItemFullDetails.events
      let currentMediaEventRowsAfterSort = this.$utils.clone(scheduleItemFullDetails.events)

      if (mediaEventsBeforeSortMoved.smartAdds) {
        scheduleItemFullDetailsEvents = scheduleItemFullDetails.addsEvents
        currentMediaEventRowsAfterSort = this.$utils.clone(scheduleItemFullDetails.addsEvents)
      }

      const newMovedMediaEventRow = currentMediaEventRowsAfterSort[sortEvtVal.newIndex]
      const topSiblingDetails = (currentMediaEventRowsAfterSort[sortEvtVal.newIndex - 1]) ? currentMediaEventRowsAfterSort[sortEvtVal.newIndex - 1] : false
      const bottomSiblingDetails = (currentMediaEventRowsAfterSort[sortEvtVal.newIndex + 1]) ? currentMediaEventRowsAfterSort[sortEvtVal.newIndex + 1] : false
      const topSiblingOrder = topSiblingDetails ? topSiblingDetails.order : false
      const bottomSiblingOrder = bottomSiblingDetails ? bottomSiblingDetails.order : false
      const currentIndexInList = sortEvtVal.newIndex
      let newCreatedOrder = this.$utils.calculateSortOrderValue({ topSiblingOrder, bottomSiblingOrder, previousOrderVal })

      const uidTarget = (scheduleItemFullDetailsEvents[currentIndexInList].uid) ? scheduleItemFullDetailsEvents[currentIndexInList].uid : this.$utils.generateUniqueID()

      // Media Audio should always be on top
      const audioTypes = ['audio', 'audio-playlist']
      const isAudioNewMovedMediaEventRow = newMovedMediaEventRow && (audioTypes.indexOf(newMovedMediaEventRow._edit.media.type) >= 0)
      const isAudioBottomSiblings = (bottomSiblingDetails && bottomSiblingDetails._edit) ? audioTypes.indexOf(bottomSiblingDetails._edit.media.type) >= 0 : false
      const isAudioTopSiblings = (topSiblingDetails && topSiblingDetails._edit) ? audioTypes.indexOf(topSiblingDetails._edit.media.type) >= 0 : false
      const isOverlayTopSiblings = topSiblingDetails && topSiblingDetails.skin

      if (isAudioNewMovedMediaEventRow && (audioTypes.indexOf(topSiblingDetails._edit.media.type) < 0)) {
        if (mediaEventsBeforeSortMoved.smartAdds) {
          this.scheduleItemFullDetails.addsEvents = mediaEventsBeforeSortMoved.events
        } else {
          this.scheduleItemFullDetails.events = mediaEventsBeforeSortMoved.events
        }

        this.mediaEventsBeforeSortMoved = { events: [] }
        return
      }

      if (!isAudioNewMovedMediaEventRow && isAudioBottomSiblings) {
        if (this.mediaEventsBeforeSortMoved.smartAdds) {
          this.scheduleItemFullDetails.addsEvents = mediaEventsBeforeSortMoved.events
        } else {
          this.scheduleItemFullDetails.events = mediaEventsBeforeSortMoved.events
        }

        this.mediaEventsBeforeSortMoved = { events: [] }
        return
      }

      /**
       * Adjust order value if top is audio or overlay.The ideas is to use the bottom order as previous order value and subtract (Jan 2018 - random number)
       * to make it into the top of the current bottom media
       */
      if (isAudioTopSiblings || isOverlayTopSiblings) {
        newCreatedOrder = this.$utils.calculateSortOrderValue({ forceToTop: true, previousOrderVal: bottomSiblingOrder })
      }

      // Media overlay or skin should always be on top after audio
      if ((bottomSiblingDetails && bottomSiblingDetails.skin) || (newMovedMediaEventRow && newMovedMediaEventRow.skin)) {
        if (this.mediaEventsBeforeSortMoved.smartAdds) {
          this.scheduleItemFullDetails.addsEvents = mediaEventsBeforeSortMoved.events
        } else {
          this.scheduleItemFullDetails.events = mediaEventsBeforeSortMoved.events
        }

        this.mediaEventsBeforeSortMoved = { events: [] }
        return
      }

      scheduleItemFullDetailsEvents[currentIndexInList].order = newCreatedOrder
      this.scheduleItemFullDetails = this.filterScheduleItemFullDetailsMediaEventSelection(scheduleItemFullDetails)

      // Make sure media schedule data is updated in media menu
      if (scheduleItemFullDetailsEvents[currentIndexInList]._edit.isRowSelected) {
        this.emitEventToParent({
          action: this.mediaEventRowActionOptions.playtimeOrderUpdated,
          addParams: {
            uid: uidTarget,
            previousOrder: previousOrderVal,
            newOrder: newCreatedOrder
          }
        })
      }

      const getAndUpdateMediaDetailsInBackend = () => {
        this.$api.get(`${CONFIG.API_BASE_URL}/media/${newMovedMediaEventRow.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`)
          .then(response => {
            const mediaItem = response.data
            const targetScheduleIndx = mediaItem.schedules.findIndex((schedule) => { return schedule.target === this.scheduleItemOrig.scheduleId })
            const targetSchedule = mediaItem.schedules[targetScheduleIndx]

            // Convert old daily schedule
            if (targetSchedule.type === 'daily') {
              targetSchedule.type = 'custom'
              targetSchedule.custom = [{
                daily: true,
                order: newCreatedOrder,
                uid: uidTarget
              }]
            }

            if (!this.sortingRequests.filter((request) => request.order === newCreatedOrder).length) {
              this.sortingRequests.push({
                order: newCreatedOrder
              })
            }

            let customPlaytimeIndx = targetSchedule.custom.findIndex((playtime) => { return playtime.uid === uidTarget })
            if (!customPlaytimeIndx) {
              customPlaytimeIndx = targetSchedule.custom.findIndex((playtime) => { return playtime.order === previousOrderVal })
            }

            if (customPlaytimeIndx >= 0) {
              mediaItem.schedules[targetScheduleIndx].custom[customPlaytimeIndx].order = newCreatedOrder
            }

            const updateMediaInBackend = () => {
              // Remove status and durtion
              if (mediaItem.status) {
                delete mediaItem.status
                delete mediaItem.dur
              }

              this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`, mediaItem)
                .then(response => {
                  this.sortingRequests = this.sortingRequests.filter((request) => request.order !== newCreatedOrder)
                })
                .catch((error) => {
                  this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { updateMediaInBackend() } })
                })
            }
            updateMediaInBackend()
          })
          .catch((error) => {
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { getAndUpdateMediaDetailsInBackend() } })
          })
      }
      getAndUpdateMediaDetailsInBackend()
    },
    handleSVGMediaFileOnload () {
      this.addMessageListener()
    },
    handleSVGMessage (event) {
      // Make sure event.origin is from the backend to prevent accepting message out of nowhere and event.data is an object
      const sessionStage = useCognitoAuthStore().sessionStage ?? CONFIG.GLOBAL_STAGE;
      const apiOrigin = (new URL(this.$utils.getStageAPIUrl(sessionStage))).origin
      if ((event.origin !== apiOrigin) || (!_.isPlainObject(event.data))) return

      // Media upload finished
      if ((typeof event.data.media !== 'undefined') && (event.data.status === 'finished')) {
        const newUploadedMedia = event.data.media
        const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)
        const currentMediaIndx = scheduleItemFullDetails.media.findIndex((currentMedia) => { return newUploadedMedia.resourceId === currentMedia.resourceId })

        if (currentMediaIndx >= 0) {
          const updatedMedia = scheduleItemFullDetails.media[currentMediaIndx]
          updatedMedia.preview = newUploadedMedia.preview
          updatedMedia.height = newUploadedMedia.height
          updatedMedia.width = newUploadedMedia.width
          updatedMedia.href = newUploadedMedia.href

          if (updatedMedia.status) {
            delete updatedMedia.status
          }
          scheduleItemFullDetails.media[currentMediaIndx] = newUploadedMedia
        }

        if (scheduleItemFullDetails.addsEvents) {
          scheduleItemFullDetails.addsEvents = this.filterAddsEvents(scheduleItemFullDetails.addsEvents)
        }

        this.scheduleItemFullDetails = this.filterScheduleItemFullDetails(scheduleItemFullDetails)
      }

      // Listen schedule preview status
      if (event.data.serialNumber === this.scheduleItemOrig.scheduleId) {
        const errorStatus = ['unconfigured', 'failed', 'disconnected']
        this.scheduleItemOrig._isInErrorStatus = errorStatus.indexOf(event.data.status) >= 0
        this.scheduleItemOrig._playerStatus = event.data.status
        this.scheduleItemOrig = this.filterScheduleItemOrig(this.scheduleItemOrig)
      }
    },
    handleSVGOnLoad () {
      this.postMessageToSVG({ type: 'iframeOnLoad' })
    },
    postMessageToSVG ({ type }) {
      const selectedSchedulePreviewEl = $(`#selectedSchedulePreview-${this.scheduleItemOrig.scheduleId}`)

      if (selectedSchedulePreviewEl.length) {
        const msg = { activeSchedulePreview: this.scheduleItemOrig.scheduleId, visible: true }

        if (type === 'documentHidden') {
          msg.visible = false
        }

        if (type === 'iframeOnLoad') {
          msg.visible = true
          if (!this.$currentUser.hasGroupDebug()) console.log('update snapshot ', this.scheduleItemOrig.name)
        }

        selectedSchedulePreviewEl[0].contentWindow.postMessage(msg, '*')
      }
    },
    addMessageListener () {
      this.handleSVGMessageEventListenerFunction = this.handleSVGMessage.bind(this)
      window.addEventListener('message', this.handleSVGMessageEventListenerFunction)
    },
    removeMessageListener () {
      if (this.handleSVGMessageEventListenerFunction) {
        window.removeEventListener('message', this.handleSVGMessageEventListenerFunction)
        this.handleSVGMessageEventListenerFunction = null
      }
    },
    handlePopIdHover ($event, type) {
      const targetEl = $($($event.target).parent())
      const textEl = $($($event.target).parent()).find('.text')
      let showTooltip = textEl.length && (textEl[0].offsetWidth < textEl[0].scrollWidth)

      if (type === 'mouseover' && showTooltip) {
        showTooltip = true
      } else {
        showTooltip = false
      }

      this.$utils.handleCustomTooltip({ targetEl, showTooltip, fullText: textEl.text() })
    },
    /**
     * Hook function for draggable as dropzone for `tags-shared` group
     *
     * @param {Object} event - VueDraggable add event
     * @param {Object} item - Schedule item
     */
    handleTagDraggableAdd (event, item) {
      const draggedEl = event.item // element being dragged
      const draggedDataset = draggedEl?.dataset // data-* attributes
      const dataDraggableType = draggedDataset?.draggableType ?? draggedEl.getAttribute('data-draggable-type') // 'tag' or 'popId'
      const dataDraggableValue = draggedDataset?.draggableValue ?? draggedEl.getAttribute('data-draggable-value') // tag or popId value

      const mediaRowEl = event.to.parentElement
      if (!draggedEl || !dataDraggableType || !dataDraggableValue || !mediaRowEl) {
        return
      }

      const newTag = dataDraggableType === 'tag' ? `tag-${dataDraggableValue}` : null // Add tag- for backend rules
      const isSmartAddsRow = mediaRowEl.classList.contains('smart-adds-schedule-row')
      const getEventUidFromClassname = mediaRowEl.className.toString().match(/(\beventUid\S+\b)/ig)
      const eventUid = item.uid
      const scheduleItemFullDetails = this.$utils.clone(this.scheduleItemFullDetails)
      let getEventIndx = scheduleItemFullDetails.events.findIndex((event) => { return event.uid === eventUid })
      let targetEvent = scheduleItemFullDetails.events[getEventIndx]
      let proceedToAddTag = false

      const disableEventRowHoverForSecond = (getEventUidFromClassname) => {
        if (getEventUidFromClassname.length) {
          this.disableMouseoverClass = getEventUidFromClassname[0]
          setTimeout(() => {
            this.disableMouseoverClass = null
          }, 5000)
        }
      }

      if (isSmartAddsRow) {
        getEventIndx = scheduleItemFullDetails.addsEvents.findIndex((event) => { return event.uid === eventUid })
        targetEvent = scheduleItemFullDetails.addsEvents[getEventIndx]

        if (targetEvent.tag !== newTag) {
          proceedToAddTag = true
          scheduleItemFullDetails.addsEvents[getEventIndx].tag = newTag
          scheduleItemFullDetails.addsEvents = this.filterAddsEvents(scheduleItemFullDetails.addsEvents)
          disableEventRowHoverForSecond(getEventUidFromClassname)
        }
      } else {
        if (targetEvent.tag !== newTag) {
          proceedToAddTag = true
          scheduleItemFullDetails.events[getEventIndx].tag = newTag
          disableEventRowHoverForSecond(getEventUidFromClassname)
        }
      }

      this.scheduleItemFullDetails = this.filterScheduleItemFullDetails(scheduleItemFullDetails)

      const targetMedia = getEventIndx >= 0 ? targetEvent._edit.media : null
      const getAndUpdateMediaDetailsInBackend = () => {
        this.$utils.mainLoader(false)
        this.$api.get(`${CONFIG.API_BASE_URL}/media/${targetMedia.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`)
          .then(response => {
            const mediaItem = response.data
            const targetScheduleIndx = mediaItem.schedules.findIndex((schedule) => { return schedule.target === this.scheduleItemOrig.scheduleId })
            const targetSchedule = (targetScheduleIndx >= 0) ? mediaItem.schedules[targetScheduleIndx] : null
            const playtimeIndexFromCustom = targetSchedule.custom.findIndex((playtime) => { return playtime.uid === eventUid })
            const playtime = targetSchedule.custom[playtimeIndexFromCustom]
            playtime.tag = newTag
            targetEvent.tag = newTag

            if (scheduleItemFullDetails.addsEvents) scheduleItemFullDetails.addsEvents = this.filterAddsEvents(scheduleItemFullDetails.addsEvents)
            this.scheduleItemFullDetails = this.filterScheduleItemFullDetails(scheduleItemFullDetails)

            const updateMediaInBackend = () => {
              // Remove status and durtion
              if (mediaItem.status) {
                delete mediaItem.status
                delete mediaItem.dur
              }

              this.$api.put(`${CONFIG.API_BASE_URL}/media/${mediaItem.resourceId}?scheduleId=${this.scheduleItemOrig.scheduleId}`, mediaItem)
                .then(response => {
                })
                .catch((error) => {
                  this.$apiError.handleAPIRequestError(error, {
                    originalRequest: () => { updateMediaInBackend() },
                    callback: () => {},
                    showDialogNotification: true
                  })
                  this.$utils.mainLoader(false)
                })
            }
            updateMediaInBackend()
          })
          .catch((error) => {
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { getAndUpdateMediaDetailsInBackend() } })
          })
      }

      if (proceedToAddTag) getAndUpdateMediaDetailsInBackend()
    }
  },
  updated () {
    // Coalesce all reactive updates within the same frame into a single scroll sync.
    // This covers every DOM patch cause (sort, row selection, sortingRequests class
    // binding changes, API-driven updates, etc.) without redundant DOM work.
    cancelAnimationFrame(this._syncRaf)
    this._syncRaf = requestAnimationFrame(() => { this.updateScheduledMediaTimeline() })
  },
  beforeUnmount () {
    cancelAnimationFrame(this._syncRaf)
    this.removeMessageListener()
    $('#mediaDialogInSchedulePage').remove() // Remove appended modal

    if (_.isFunction(this.cleanupVisibilityListenerCallback)) {
      this.cleanupVisibilityListenerCallback()
    }
  },
  created () {
    this._syncRaf = null
    this.handleSmartAddsSponsoredActionClick = _.debounce(this.handleSmartAddsSponsoredActionClick, 500)
  },
  mounted () {
    this.addMessageListener()

    this.setDefaultData()
    $('#mediaDialogInSchedulePage').appendTo($('#schedulePage')) // A workaround to show modal in entire screen for all browsers.Safari has issue in display fixed for child element

    // Send a postMessage to the selected schedule SVG template to disable snapshots when the page/tab is not visible, and enable them again when it becomes visible.
    this.cleanupVisibilityListenerCallback = this.$utils.handleDocumentVisibilityChange(
      () => this.postMessageToSVG({ type: 'documentHidden' }),
      () => this.postMessageToSVG({ type: 'documentVisible' })
    )

    // Hide dialog when pressing escape
    this.$utils.detectEscapeKeyPressed(() => { this.handleDialogClose() })
  }
}
